import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useNotesAuth } from '@/lib/notesAuth';
import { notesApi } from '@/lib/notesApi';
import NoteEditor from './NoteEditor';
import { ModuleSwitch } from './ModuleSwitch';
import { CommandPalette } from './CommandPalette';
import { PushPrompt, PushBell } from './PushPrompt';
import PlannerReminderAlerts from './PlannerReminderAlerts';
import PlannerNavShortcuts from './PlannerNavShortcuts';
import ChatDock from './ChatDock';
import { useChatEnabled } from './useChatEnabled';
import {
  Modal, BtnGhost, FolderDialog, TemplatePickerDialog, TemplateManagerDialog, AdminUsersDialog, ShareDialog,
} from './NotesModals';
import {
  Feather, Plus, Search, Star, FileText, Folder, FolderPlus, FolderOpen, Sun, Moon, LogOut, Users, Users2,
  Loader2, Trash2, Pencil, BookOpen, Paperclip, ChevronDown, ChevronRight, ArrowLeft, Layers, X, Hash, Clock,
  PanelLeftClose, PanelLeftOpen, ArrowDownAZ, ArrowUpAZ, Inbox, Eye, Maximize2, Minimize2, Bell, Share2, BellRing, CalendarClock, RefreshCw, Pause, Play,
  Bookmark, BookmarkCheck, BookmarkPlus, Menu, ChevronLeft, ListChecks, Pin, PinOff, Command, CornerDownLeft, MessageSquare,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
// Small local id generator for client-only records (e.g. pinned tag combos).
const genId = () => (typeof crypto !== 'undefined' && crypto.randomUUID)
  ? crypto.randomUUID()
  : `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 45) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 172800) return 'yesterday';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// Highlight every case-insensitive occurrence of `term` inside `text`, wrapping
// matches in a subtle accent mark. Used by the search dropdown + command palette.
function Highlight({ text, term }) {
  const src = text || '';
  const q = (term || '').trim();
  if (!q) return <>{src}</>;
  let parts;
  try {
    parts = src.split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig'));
  } catch {
    return <>{src}</>;
  }
  const low = q.toLowerCase();
  return (
    <>
      {parts.map((p, i) => (p.toLowerCase() === low
        ? <mark key={i} className="rounded-[3px] bg-[var(--n-accent-bg)] px-0.5 text-[var(--n-accent)]">{p}</mark>
        : <React.Fragment key={i}>{p}</React.Fragment>))}
    </>
  );
}

// Format an epoch-ms as a local "YYYY-MM-DDTHH:mm" string for <input type="datetime-local">.
function toLocalInputValue(ms) {
  const d = new Date(ms);
  const pad = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Group folders by parent id for tree rendering.
function buildChildrenMap(folders) {
  const map = {};
  (folders || []).forEach((f) => {
    const p = f.parent_id || '__root';
    (map[p] = map[p] || []).push(f);
  });
  return map;
}

// Depth-first flatten preserving name order, annotating each folder with its
// nesting depth (used by the "move to folder" menu).
function flattenFolderTree(folders) {
  const map = buildChildrenMap(folders);
  const out = [];
  const walk = (parent, depth) => {
    (map[parent] || []).forEach((f) => { out.push({ ...f, depth }); walk(f.id, depth + 1); });
  };
  walk('__root', 0);
  return out;
}

// ---------------------------------------------------------------------------
// Small internal pieces
// ---------------------------------------------------------------------------
function ConfirmDialog({ open, title, message, confirmLabel, danger, onConfirm, onClose }) {
  return (
    <Modal open={open} onClose={onClose} title={title} maxW="max-w-sm" testid="notes-confirm-dialog"
      footer={<>
        <BtnGhost onClick={onClose} data-testid="notes-confirm-cancel">Cancel</BtnGhost>
        <button onClick={onConfirm} data-testid="notes-confirm-accept"
          className={`inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-semibold text-white hover:opacity-90 ${danger ? 'bg-[var(--n-danger)]' : 'bg-[var(--n-ink)]'}`}>
          {confirmLabel || 'Confirm'}
        </button>
      </>}>
      <p className="text-sm text-[var(--n-ink-soft)] py-1 leading-relaxed">{message}</p>
    </Modal>
  );
}

function MoveFolderMenu({ folders, sharedFolders = [], value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const tree = flattenFolderTree(folders);
  const current = folders.find((f) => f.id === value) || sharedFolders.find((f) => f.id === value);
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} data-testid="note-folder-menu-button"
        className="n-focus inline-flex items-center gap-1.5 rounded-full border border-[var(--n-border)] px-2.5 py-1.5 text-[12px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)] transition-colors">
        <Folder size={13} className="text-[var(--n-accent)]" /> {current ? current.name : 'No folder'} <ChevronDown size={13} />
      </button>
      {open && (
        <div className="absolute left-0 z-50 mt-1 w-56 max-h-72 overflow-y-auto n-scroll rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1 shadow-[var(--n-shadow)]" data-testid="note-folder-menu">
          <button onClick={() => { onChange(''); setOpen(false); }}
            className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm hover:bg-[var(--n-canvas-2)] ${!value ? 'text-[var(--n-accent)]' : 'text-[var(--n-ink-soft)]'}`}>
            <Layers size={14} /> No folder
          </button>
          {tree.map((f) => (
            <button key={f.id} onClick={() => { onChange(f.id); setOpen(false); }}
              style={{ paddingLeft: 10 + f.depth * 14 }}
              className={`flex w-full items-center gap-2 rounded-lg pr-2.5 py-2 text-left text-sm hover:bg-[var(--n-canvas-2)] ${value === f.id ? 'text-[var(--n-accent)]' : 'text-[var(--n-ink-soft)]'}`}>
              <Folder size={14} className="shrink-0" style={f.color ? { color: f.color } : undefined} /> <span className="truncate">{f.name}</span>
            </button>
          ))}
          {sharedFolders.length > 0 && (
            <>
              <div className="mx-2 my-1 border-t border-[var(--n-line)]" />
              <div className="px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">Shared with me</div>
              {sharedFolders.map((f) => (
                <button key={f.id} data-testid="note-folder-menu-shared-option" onClick={() => { onChange(f.id); setOpen(false); }}
                  className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm hover:bg-[var(--n-canvas-2)] ${value === f.id ? 'text-[var(--n-accent)]' : 'text-[var(--n-ink-soft)]'}`}>
                  <Users2 size={14} className="shrink-0 text-[var(--n-accent)]" /> <span className="truncate">{f.name}</span>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function NoteCard({ note, active, onClick, onDragStart, draggable = true, onTogglePin, showFolder = false }) {
  const overdue = note.reminder_at && new Date(note.reminder_at).getTime() <= Date.now();
  const pinned = !!note.pinned;
  return (
    <button onClick={onClick} data-testid="note-list-item"
      draggable={draggable} onDragStart={draggable ? onDragStart : undefined}
      className={`group relative w-full text-left px-3.5 py-3 transition-colors ${draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}
        ${active ? 'bg-[var(--n-accent-bg)]' : 'hover:bg-[var(--n-surface-2)]'}`}>
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-display text-[15px] font-semibold leading-snug text-[var(--n-ink)] line-clamp-1">{note.title || 'Untitled'}</h3>
        <span className="mt-0.5 flex shrink-0 items-center gap-1">
          {note.is_favorite && <Star size={13} className="shrink-0 fill-[var(--n-accent)] text-[var(--n-accent)]" />}
          {pinned && <Pin size={13} className="shrink-0 fill-[var(--n-accent)] text-[var(--n-accent)]" data-testid="note-pin-indicator" aria-label="Pinned" />}
          {onTogglePin && (
            <span role="button" tabIndex={0} data-testid="note-pin-toggle"
              title={pinned ? 'Unpin note' : 'Pin note'} aria-label={pinned ? 'Unpin note' : 'Pin note'}
              onClick={(e) => { e.stopPropagation(); onTogglePin(note); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onTogglePin(note); } }}
              className={`n-focus grid h-6 w-6 place-items-center rounded-md text-[var(--n-muted-2)] transition-opacity hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-accent)] ${pinned ? 'opacity-0 group-hover:opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'}`}>
              {pinned ? <PinOff size={13} /> : <Pin size={13} />}
            </span>
          )}
        </span>
      </div>
      {note.snippet && <p className="mt-1 text-[12.5px] leading-relaxed text-[var(--n-muted)] line-clamp-2">{note.snippet}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-[var(--n-muted-2)]">
        {showFolder && note.folder_name && (
          <span className="inline-flex items-center gap-1 rounded-full bg-[var(--n-canvas-2)] px-1.5 py-0.5 font-medium text-[var(--n-ink-soft)]" data-testid="note-folder-label">
            <Folder size={10} /> {note.folder_name}
          </span>
        )}
        <span>{relTime(note.updated_at)}</span>
        {note.has_attachments && <Paperclip size={11} />}
        {note.reminder_at && <Clock size={11} className={overdue ? 'text-[var(--n-danger)]' : 'text-[var(--n-accent)]'} />}
        {note.shared_by && <span className="inline-flex items-center gap-1 text-[var(--n-accent)]"><Users2 size={11} /> {note.shared_by}</span>}
        {(note.tags || []).slice(0, 2).map((t) => (
          <span key={t} className="rounded-full bg-[var(--n-canvas-2)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--n-ink-soft)]">#{t}</span>
        ))}
      </div>
    </button>
  );
}

function RailItem({ icon: Icon, label, count, active, onClick, testid, badge }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`n-focus flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors
        ${active ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)] font-semibold' : 'text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
      <Icon size={16} strokeWidth={1.8} />
      <span className="flex-1 truncate text-left">{label}</span>
      {badge > 0 && (
        <span data-testid="notes-shared-badge"
          className="min-w-[18px] rounded-full bg-[var(--n-accent)] px-1.5 text-center text-[10px] font-bold leading-[18px] text-white">{badge}</span>
      )}
      {typeof count === 'number' && <span className="text-[11px] text-[var(--n-muted-2)] tabular-nums">{count}</span>}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Reminder alarm — a short, attention-grabbing WAV built in-browser (no asset).
// Played through an <audio> element that we "unlock" on the first user gesture,
// which is the reliable way to make sound fire later on iOS/Safari.
// ---------------------------------------------------------------------------
let _alarmSrc = null;
function alarmDataUri() {
  if (_alarmSrc) return _alarmSrc;
  const sr = 8000;
  const pattern = [
    { f: 988, d: 0.16 }, { f: 0, d: 0.05 },
    { f: 1319, d: 0.16 }, { f: 0, d: 0.05 },
    { f: 988, d: 0.16 }, { f: 0, d: 0.05 },
    { f: 1319, d: 0.16 }, { f: 0, d: 0.12 },
    { f: 988, d: 0.16 }, { f: 0, d: 0.05 },
    { f: 1319, d: 0.28 },
  ];
  let total = 0;
  pattern.forEach((b) => { total += Math.floor(b.d * sr); });
  const samples = new Int16Array(total);
  let idx = 0;
  pattern.forEach((b) => {
    const n = Math.floor(b.d * sr);
    for (let i = 0; i < n; i++) {
      let s = 0;
      if (b.f > 0) {
        const env = Math.min(1, i / 48) * Math.min(1, (n - i) / 48);
        s = Math.sin(2 * Math.PI * b.f * (i / sr)) * env * 0.92;
      }
      samples[idx++] = Math.max(-1, Math.min(1, s)) * 32767;
    }
  });
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const w = (o, str) => { for (let i = 0; i < str.length; i++) view.setUint8(o + i, str.charCodeAt(i)); };
  w(0, 'RIFF'); view.setUint32(4, 36 + samples.length * 2, true); w(8, 'WAVE');
  w(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, sr, true); view.setUint32(28, sr * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
  w(36, 'data'); view.setUint32(40, samples.length * 2, true);
  let off = 44;
  for (let i = 0; i < samples.length; i++) { view.setInt16(off, samples[i], true); off += 2; }
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  _alarmSrc = 'data:audio/wav;base64,' + btoa(binary);
  return _alarmSrc;
}

// ---------------------------------------------------------------------------
// Main shell
// ---------------------------------------------------------------------------
export default function NotesAppShell() {
  const { principal, logout, theme, toggleTheme, isSuper } = useNotesAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [folders, setFolders] = useState([]);
  const [totalNotes, setTotalNotes] = useState(0);
  const [favoriteCount, setFavoriteCount] = useState(0);
  const [updatedTodayCount, setUpdatedTodayCount] = useState(0);
  const [pinnedCount, setPinnedCount] = useState(0);
  const [tags, setTags] = useState([]);
  const [notes, setNotes] = useState([]);
  const [sharedFolders, setSharedFolders] = useState([]);
  const [view, setView] = useState('all'); // 'all' | 'favorites' | 'updated_today' | 'shared' | 'shared:<fid>' | <folderId>
  const [selectedTags, setSelectedTags] = useState([]);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [notesLoading, setNotesLoading] = useState(true);
  const [noteLoading, setNoteLoading] = useState(false);

  // Dialogs
  const [folderDialog, setFolderDialog] = useState({ open: false, initial: null, parentId: null });
  const [templatePicker, setTemplatePicker] = useState(false);
  const [templateManager, setTemplateManager] = useState(false);
  const [adminUsers, setAdminUsers] = useState(false);
  const [confirm, setConfirm] = useState({ open: false });
  const [shareDialog, setShareDialog] = useState({ open: false, resource: null });

  // Folder tree — collapsed set (folders NOT in the set are expanded).
  const [collapsed, setCollapsed] = useState(() => new Set());
  const toggleCollapse = useCallback((id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // Tags panel — inline "add tag" affordance.
  const [tagAdding, setTagAdding] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [tagEditing, setTagEditing] = useState(null); // tag currently being renamed
  const [tagEditVal, setTagEditVal] = useState('');
  // Rail tag search — filters the tag list (and the cascade candidates).
  const [tagQuery, setTagQuery] = useState('');
  // Pinned tag combinations (favourites) — persisted locally so a favourite
  // AND-combo can be re-applied in one click. Each entry is { id, tags[], label }.
  // Backward compatible with the earlier shape (a bare array of tags).
  const [pinnedCombos, setPinnedCombos] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem('notes:pinnedTagCombos') || '[]');
      if (!Array.isArray(raw)) return [];
      return raw
        .map((c) => (Array.isArray(c)
          ? { id: genId(), tags: c, label: '' }
          : (c && Array.isArray(c.tags) ? { id: c.id || genId(), tags: c.tags, label: c.label || '' } : null)))
        .filter((c) => c && c.tags.length);
    } catch { return []; }
  });
  const [editingComboId, setEditingComboId] = useState(null);
  const [editingLabel, setEditingLabel] = useState('');

  // Search combobox — suggestion dropdown (notes + tags).
  const [searchOpen, setSearchOpen] = useState(false);
  const [cmdkOpen, setCmdkOpen] = useState(false);
  const [noteSug, setNoteSug] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [coTagCounts, setCoTagCounts] = useState(null); // tags co-occurring with the active selection (AND-aware)
  const searchRef = useRef(null);

  // Layout / preferences (persisted).
  const [railHidden, setRailHidden] = useState(() => localStorage.getItem('notes:railHidden') === '1');
  const [folderPeekOpen, setFolderPeekOpen] = useState(false); // collapsed-rail folders flyout (temporary peek)
  const [mobileRailOpen, setMobileRailOpen] = useState(false); // mobile folders/tags drawer
  const [tagSort, setTagSort] = useState(() => localStorage.getItem('notes:tagSort') || 'az');
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [focusMode, setFocusMode] = useState(() => localStorage.getItem('notes:focus') === '1');
  const [colorFilter, setColorFilter] = useState(null); // folder color filter
  const [railSec, setRailSec] = useState(() => {
    try { return JSON.parse(localStorage.getItem('notes:railSec')) || { folders: true, tags: true }; }
    catch { return { folders: true, tags: true }; }
  });
  const toggleRailSec = useCallback((k) => setRailSec((s) => {
    const nx = { ...s, [k]: !s[k] };
    try { localStorage.setItem('notes:railSec', JSON.stringify(nx)); } catch { /* noop */ }
    return nx;
  }), []);
  const toggleRail = useCallback(() => { setFolderPeekOpen(false); setRailHidden((v) => { const n = !v; localStorage.setItem('notes:railHidden', n ? '1' : '0'); return n; }); }, []);
  const toggleTagSort = useCallback(() => setTagSort((s) => { const n = s === 'az' ? 'za' : 'az'; localStorage.setItem('notes:tagSort', n); return n; }), []);
  const toggleFocus = useCallback(() => setFocusMode((v) => { const n = !v; localStorage.setItem('notes:focus', n ? '1' : '0'); return n; }), []);
  // Mobile: return from the note editor to the note list.
  const backToList = useCallback(() => { setSelectedId(null); setSelectedNote(null); }, []);
  // Mobile: close the folders/tags drawer whenever the active view changes.
  useEffect(() => { setMobileRailOpen(false); setFolderPeekOpen(false); }, [view]);

  // Upcoming reminders panel
  const [remindersOpen, setRemindersOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(() => {
    // A docked chat stays present across Notes ↔ Tasks; auto-open on mount.
    try { return localStorage.getItem('chat_docked') === '1'; } catch { return false; }
  });
  const [chatUnread, setChatUnread] = useState(0);
  const [chatReserve, setChatReserve] = useState(0); // right padding when chat is docked
  // Chat is hidden entirely while Maintenance Lockdown is ON (Super Admin).
  const { enabled: chatEnabled, enabledRef: chatEnabledRef } = useChatEnabled();
  useEffect(() => { try { if (chatEnabled && new URLSearchParams(window.location.search).get('chat')) setChatOpen(true); } catch { /* noop */ } }, [chatEnabled]);
  useEffect(() => { if (!chatEnabled) { setChatOpen(false); setChatReserve(0); } }, [chatEnabled]);
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const remindersRef = useRef(null);

  // Local themed toast (optionally clickable via `action`).
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const notify = useCallback((message, action) => {
    setToast({ message, action });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), action ? 6000 : 2600);
  }, []);

  // Real-time collaboration refs
  const wsRef = useRef(null);
  const handlerRef = useRef(null);
  const selectedIdRef = useRef(null);
  const selectedNoteRef = useRef(null);
  const subscribedRef = useRef(null);
  const reloadTimerRef = useRef(null);
  const localSaveRef = useRef({}); // noteId -> ts of my last local write (echo suppression)
  const [remoteReloadKey, setRemoteReloadKey] = useState(0);
  const [presence, setPresence] = useState({}); // noteId -> [{id,name}]
  const [dueQueue, setDueQueue] = useState([]); // currently-due, unacknowledged reminders (stacked)
  const [customSnoozeKey, setCustomSnoozeKey] = useState(null); // remKey of the alert whose custom picker is open
  const [customSnoozeVal, setCustomSnoozeVal] = useState(''); // datetime-local value for the custom snooze picker
  const [notifPerm, setNotifPerm] = useState(() => (typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'));
  const soundedRef = useRef(new Set()); // reminder keys already alerted (OS + sound) this session
  const audioCtxRef = useRef(null);
  const alarmRef = useRef(null); // unlocked <audio> element for the reminder alarm
  const alarmUnlockedRef = useRef(false);
  const primedRef = useRef(false); // first poll only primes (no chime) so opening never sounds
  const openNoteRef = useRef(null); // set after openNote is defined (avoids TDZ in reminder helpers)
  const [sharedInbox, setSharedInbox] = useState(0); // "new shares" badge count

  const tagKey = selectedTags.join('|');
  const isSharedView = view === 'shared' || view.startsWith('shared:');
  const isPinnedView = view === 'pinned';
  // Pinned notes always float to the top of a folder/list view, regardless of
  // the (updated-desc) order the server returns. The dedicated Pinned view is
  // already ordered by the server, so we leave it untouched there.
  const displayNotes = useMemo(() => {
    if (isPinnedView) return notes;
    const pinned = [];
    const rest = [];
    for (const n of notes) (n.pinned ? pinned : rest).push(n);
    return pinned.length ? [...pinned, ...rest] : notes;
  }, [notes, isPinnedView]);

  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);
  useEffect(() => { selectedNoteRef.current = selectedNote; }, [selectedNote]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadFolders = useCallback(async () => {
    try {
      const { data } = await notesApi.get('/notes/folders');
      setFolders(data.folders || []);
      setTotalNotes(data.total_notes || 0);
      setFavoriteCount(data.favorite_count || 0);
      setUpdatedTodayCount(data.updated_today_count || 0);
      setPinnedCount(data.pinned_count || 0);
    } catch { /* silent */ }
  }, []);

  const loadTags = useCallback(async () => {
    try {
      const { data } = await notesApi.get('/notes/tags');
      setTags(data || []);
    } catch { /* silent */ }
  }, []);

  const loadSharedFolders = useCallback(async () => {
    try {
      const { data } = await notesApi.get('/notes/shared/folders');
      setSharedFolders(data || []);
    } catch { /* silent */ }
  }, []);

  const didInitialNotesLoadRef = useRef(false); // skeleton shows ONLY on the first load
  const loadNotes = useCallback(async (silent = false) => {
    // In-module navigation (folder/view/tag switches, edits, auto-refresh) must
    // never flash the skeleton — only the very first load does.
    const showSkeleton = !silent && !didInitialNotesLoadRef.current;
    if (showSkeleton) setNotesLoading(true);
    try {
      const params = new URLSearchParams();
      let path;
      if (view === 'pinned') {
        // "Pinned Notes" system folder — all pinned notes across every folder,
        // including shared, in one combined view (server sorts newest-pin first).
        path = '/notes/notes/pinned';
      } else if (view === 'shared' || view.startsWith('shared:')) {
        path = '/notes/shared/notes';
        if (view.startsWith('shared:')) params.append('folder_id', view.slice(7));
        if (debounced) params.append('q', debounced);
      } else {
        path = '/notes/notes';
        if (view === 'favorites') params.append('favorite', 'true');
        else if (view === 'updated_today') params.append('updated_today', 'true');
        else if (view !== 'all') params.append('folder_id', view);
        // "All Notes" is a unified inbox: include notes shared with me so their
        // content + tags are searchable/filterable alongside my own notes.
        if (view === 'all') params.append('include_shared', 'true');
        if (debounced) params.append('q', debounced);
        selectedTags.forEach((t) => params.append('tags', t));
      }
      const { data } = await notesApi.get(`${path}?${params.toString()}`);
      setNotes(data || []);
    } catch {
      setNotes([]);
    } finally {
      didInitialNotesLoadRef.current = true;
      if (showSkeleton) setNotesLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, debounced, tagKey]);

  useEffect(() => { loadFolders(); loadTags(); loadSharedFolders(); }, [loadFolders, loadTags, loadSharedFolders]);
  useEffect(() => { loadNotes(); }, [loadNotes]);

  // ---- Auto-refresh -------------------------------------------------------
  // Notes shared with you refresh on a predictable 5-minute cadence (with a
  // visible countdown + manual "refresh now" button) rather than pushing live
  // over the WebSocket. See handleWsMessage where the live list-reloads are
  // intentionally disabled in favour of this poll.
  const REFRESH_SECONDS = 300;
  const [secondsToRefresh, setSecondsToRefresh] = useState(REFRESH_SECONDS);
  const [refreshing, setRefreshing] = useState(false);
  // Users can pause the shared-notes poll while focusing; the preference sticks.
  const [refreshPaused, setRefreshPaused] = useState(() => {
    try { return localStorage.getItem('notes_refresh_paused') === '1'; } catch { return false; }
  });
  const refreshPausedRef = useRef(refreshPaused);
  useEffect(() => { refreshPausedRef.current = refreshPaused; }, [refreshPaused]);
  const nextRefreshRef = useRef(Date.now() + REFRESH_SECONDS * 1000);
  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    try { await Promise.all([loadNotes(), loadFolders(), loadTags(), loadSharedFolders()]); }
    catch { /* silent */ }
    finally {
      setRefreshing(false);
      nextRefreshRef.current = Date.now() + REFRESH_SECONDS * 1000;
      setSecondsToRefresh(REFRESH_SECONDS);
    }
  }, [loadNotes, loadFolders, loadTags, loadSharedFolders]);
  const toggleRefreshPause = useCallback(() => {
    setRefreshPaused((p) => {
      const nx = !p;
      try { localStorage.setItem('notes_refresh_paused', nx ? '1' : '0'); } catch { /* noop */ }
      // On resume, restart a fresh full countdown so there's no surprise reload.
      if (!nx) { nextRefreshRef.current = Date.now() + REFRESH_SECONDS * 1000; setSecondsToRefresh(REFRESH_SECONDS); }
      return nx;
    });
  }, []);
  useEffect(() => {
    const iv = setInterval(() => {
      if (refreshPausedRef.current) return; // frozen while paused — no countdown, no auto reload
      const left = Math.max(0, Math.round((nextRefreshRef.current - Date.now()) / 1000));
      setSecondsToRefresh(left);
      if (left <= 0) { nextRefreshRef.current = Date.now() + REFRESH_SECONDS * 1000; refreshAll(); }
    }, 1000);
    return () => clearInterval(iv);
  }, [refreshAll]);

  // Derived folder tree + all tag names (for editor autocomplete).
  const childrenMap = useMemo(() => buildChildrenMap(folders), [folders]);
  const sharedChildrenMap = useMemo(() => buildChildrenMap(sharedFolders), [sharedFolders]);

  // Collapse every parent folder by default (both trees). Runs once, when the
  // folder lists first arrive, so the sidebar starts tidy; the user's manual
  // expand/collapse choices are preserved for the rest of the session.
  const collapsedInitRef = useRef(false);
  useEffect(() => {
    if (collapsedInitRef.current) return;
    if (!folders.length && !sharedFolders.length) return;
    const parents = new Set();
    Object.entries(childrenMap).forEach(([pid, kids]) => { if (pid !== '__root' && (kids || []).length) parents.add(pid); });
    Object.entries(sharedChildrenMap).forEach(([pid, kids]) => { if (pid !== '__root' && (kids || []).length) parents.add(pid); });
    setCollapsed(parents);
    collapsedInitRef.current = true;
  }, [folders, sharedFolders, childrenMap, sharedChildrenMap]);

  const allTags = useMemo(() => tags.map((t) => t.tag), [tags]);
  const sortedTags = useMemo(
    () => [...tags].sort((a, b) => (tagSort === 'az' ? a.tag.localeCompare(b.tag) : b.tag.localeCompare(a.tag))),
    [tags, tagSort],
  );
  // Split into my own tags vs tags that only exist on notes shared with me, so
  // the panel can show shared-only tags under their own "Shared" group.
  const ownTags = useMemo(() => sortedTags.filter((t) => !t.shared_only), [sortedTags]);
  const sharedOnlyTags = useMemo(() => sortedTags.filter((t) => t.shared_only), [sortedTags]);

  // ── Rail tag search + cascade (AND) filtering ──────────────────────────
  // When one or more tags are selected we switch the rail into "cascade" mode:
  // only tags that co-occur with the current selection are shown, each carrying
  // its co-occurrence count. Everything is driven by `selectedTags` +
  // `coTagCounts` (already computed AND-aware) so nothing here refetches/reloads.
  const cascadeActive = selectedTags.length > 0;
  const tagMetaByName = useMemo(() => {
    const m = {}; tags.forEach((t) => { m[t.tag] = t; }); return m;
  }, [tags]);
  const selectedTagChips = useMemo(() => selectedTags.map((name) => ({
    tag: name,
    count: coTagCounts ? (coTagCounts[name] || 0) : (tagMetaByName[name]?.count || 0),
    shared_only: tagMetaByName[name]?.shared_only,
  })), [selectedTags, coTagCounts, tagMetaByName]);
  const cascadeTags = useMemo(() => {
    if (!cascadeActive || !coTagCounts) return [];
    return Object.keys(coTagCounts)
      .filter((tg) => !selectedTags.includes(tg))
      .map((tg) => ({ tag: tg, count: coTagCounts[tg], shared_only: tagMetaByName[tg]?.shared_only }))
      .sort((a, b) => (b.count - a.count) || a.tag.localeCompare(b.tag));
  }, [cascadeActive, coTagCounts, selectedTags, tagMetaByName]);
  const usedColors = useMemo(() => {
    const seen = [];
    folders.forEach((f) => { if (f.color && !seen.includes(f.color)) seen.push(f.color); });
    return seen;
  }, [folders]);
  const colorFilteredFolders = useMemo(
    () => (colorFilter ? folders.filter((f) => f.color === colorFilter) : []),
    [folders, colorFilter],
  );

  // ---- Reminder alerts ------------------------------------------------------
  // Due reminders raise a prominent in-app alert, a cross-tab OS/browser
  // notification, and a sound — even when this tab is in the background. When
  // the browser was closed at the due time, they surface immediately on login.
  const remKey = (n) => `${n.id}:${n.reminder_at}`;
  const ackRef = useRef(null); // reminder keys the user has explicitly dismissed (persisted)
  const getAck = () => {
    if (!ackRef.current) {
      try { ackRef.current = new Set(JSON.parse(localStorage.getItem('notes:remAck') || '[]')); }
      catch { ackRef.current = new Set(); }
    }
    return ackRef.current;
  };
  const persistAck = () => { try { localStorage.setItem('notes:remAck', JSON.stringify([...getAck()])); } catch { /* noop */ } };

  // Per-user, client-side snooze store — used for reminders on notes shared
  // with me so snoozing does NOT mutate the owner's note. Maps a reminder key
  // to the epoch-ms it should re-surface at. Persisted across reloads.
  const snoozeRef = useRef(null);
  const getSnooze = () => {
    if (!snoozeRef.current) {
      try { snoozeRef.current = JSON.parse(localStorage.getItem('notes:remSnooze') || '{}'); }
      catch { snoozeRef.current = {}; }
    }
    return snoozeRef.current;
  };
  const persistSnooze = () => { try { localStorage.setItem('notes:remSnooze', JSON.stringify(getSnooze())); } catch { /* noop */ } };
  const setLocalSnooze = (n, untilMs) => {
    const s = getSnooze(); s[remKey(n)] = untilMs; persistSnooze();
    // Allow the alarm to sound again when the snooze lapses.
    soundedRef.current.delete(remKey(n));
  };

  // Attention-grabbing reminder alert sound. Plays the unlocked <audio> alarm
  // (reliable on iOS/Safari) plus a Web Audio chime as a desktop backup, and a
  // vibration on devices that support it.
  const playChime = useCallback(() => {
    // 1) HTMLAudio alarm — the dependable path once unlocked by a gesture.
    try {
      const a = alarmRef.current;
      if (a) { a.muted = false; a.volume = 1; a.currentTime = 0; const p = a.play(); if (p && p.catch) p.catch(() => {}); }
    } catch { /* noop */ }
    // 2) Vibrate (Android / supporting browsers).
    try { if (navigator.vibrate) navigator.vibrate([250, 120, 250, 120, 400]); } catch { /* noop */ }
    // 3) Web Audio chime backup.
    try {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (!Ctor) return;
      let ctx = audioCtxRef.current;
      if (!ctx) { ctx = new Ctor(); audioCtxRef.current = ctx; }
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const base = ctx.currentTime + 0.02;
      [880, 1174.66, 1567.98, 1174.66, 1567.98].forEach((freq, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = 'triangle';
        o.frequency.value = freq;
        const t0 = base + i * 0.2;
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(0.5, t0 + 0.03);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.6);
        o.connect(g); g.connect(ctx.destination);
        o.start(t0); o.stop(t0 + 0.65);
      });
    } catch { /* audio unsupported */ }
  }, []);

  const acknowledgeReminder = useCallback((n) => {
    const k = remKey(n);
    getAck().add(k); persistAck();
    setDueQueue((q) => q.filter((x) => remKey(x) !== k));
  }, []);

  const openReminder = useCallback((n) => { acknowledgeReminder(n); openNoteRef.current?.(n.id); }, [acknowledgeReminder]);

  // Apply a snooze to `targetMs`. For notes I own, this bumps the reminder on
  // the server so it re-fires later. For notes shared with me, it snoozes
  // locally (per-user) so the owner's reminder is never modified.
  const applySnooze = useCallback(async (n, targetMs, label) => {
    setDueQueue((q) => q.filter((x) => x.id !== n.id));
    if (n.owned === false) {
      setLocalSnooze(n, targetMs);
      notify(label);
      return;
    }
    soundedRef.current.add(remKey(n));
    try {
      await notesApi.patch(`/notes/notes/${n.id}`, { reminder_at: new Date(targetMs).toISOString() });
      notify(label);
    } catch { notify('Could not snooze reminder.'); }
  }, [notify]);

  // Snooze: bump the reminder to now + delta so it fires again later.
  const snoozeReminder = useCallback((n, minutes) => (
    applySnooze(n, Date.now() + minutes * 60000, minutes >= 60 ? 'Snoozed for 1 hour.' : `Snoozed for ${minutes} minutes.`)
  ), [applySnooze]);

  // Snooze until tomorrow at 10:00 (local time).
  const snoozeUntilTomorrow = useCallback((n) => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(10, 0, 0, 0);
    return applySnooze(n, d.getTime(), 'Snoozed until tomorrow 10am.');
  }, [applySnooze]);

  // Custom snooze — snooze to an arbitrary date/time the user picks.
  const snoozeCustom = useCallback((n, localDateTimeStr) => {
    const t = new Date(localDateTimeStr).getTime();
    if (isNaN(t)) { notify('Please pick a valid date and time.'); return; }
    if (t <= Date.now()) { notify('Pick a time in the future.'); return; }
    setCustomSnoozeKey(null);
    return applySnooze(n, t, `Snoozed until ${formatWhen(new Date(t).toISOString())}.`);
  }, [applySnooze, notify]);

  // Advance a repeating reminder to its next future occurrence.
  const advanceReminderAt = (iso, repeat) => {
    const step = repeat === 'weekly' ? 7 * 86400000 : 86400000;
    let t = new Date(iso).getTime();
    if (isNaN(t)) return null;
    const now = Date.now();
    while (t <= now) t += step;
    return new Date(t).toISOString();
  };

  useEffect(() => {
    let stop = false;
    const check = async () => {
      try {
        const { data } = await notesApi.get('/notes/notes/reminders');
        if (stop) return;
        const now = Date.now();
        const ack = getAck();
        const snooze = getSnooze();
        // Prune expired local snoozes so those reminders can surface again.
        let snoozeChanged = false;
        Object.keys(snooze).forEach((k) => { if (snooze[k] <= now) { delete snooze[k]; snoozeChanged = true; } });
        if (snoozeChanged) persistSnooze();
        const due = (data || []).filter((n) => {
          if (!n.reminder_at || new Date(n.reminder_at).getTime() > now) return false;
          if (ack.has(remKey(n))) return false;
          const until = snooze[remKey(n)];
          if (until && until > now) return false; // locally snoozed (shared note)
          return true;
        });
        // Keep the visible stack in sync with what is currently due.
        setDueQueue(due);
        // Alert (OS notification + sound) once per reminder per session. The
        // FIRST poll after the module opens only primes the alerted set — it
        // never chimes for reminders that were already due when you arrived, so
        // opening Notes is silent. Only reminders that come due while the app is
        // open (a later poll) alert with sound.
        const fresh = due.filter((n) => !soundedRef.current.has(remKey(n)));
        const wasPrimed = primedRef.current;
        primedRef.current = true;
        if (fresh.length) {
          fresh.forEach((n) => soundedRef.current.add(remKey(n)));
          if (wasPrimed) {
            playChime();
            try {
              if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                fresh.forEach((n) => {
                  const notif = new Notification('Reminder due', {
                    body: n.title || 'Untitled note',
                    tag: `notes-rem-${n.id}`,
                    requireInteraction: true,
                  });
                  notif.onclick = () => { try { window.focus(); } catch { /* noop */ } openReminder(n); try { notif.close(); } catch { /* noop */ } };
                });
              }
            } catch { /* notifications unsupported */ }
          }
          // Repeating reminders: schedule the next occurrence. Only for notes I
          // own — a shared note's schedule is advanced by its owner's client.
          fresh.forEach((n) => {
            if (n.owned !== false && (n.reminder_repeat === 'daily' || n.reminder_repeat === 'weekly')) {
              const next = advanceReminderAt(n.reminder_at, n.reminder_repeat);
              if (next) notesApi.patch(`/notes/notes/${n.id}`, { reminder_at: next, reminder_repeat: n.reminder_repeat }).catch(() => {});
            }
          });
        }
      } catch { /* silent */ }
    };
    check();
    const iv = setInterval(check, 30000);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { stop = true; clearInterval(iv); document.removeEventListener('visibilitychange', onVisible); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playChime, openReminder]);

  // Request OS/browser notification permission + unlock audio on the first user
  // gesture inside the module (browsers block both without a gesture).
  const requestNotifPermission = useCallback(() => {
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
        Notification.requestPermission().then((p) => setNotifPerm(p)).catch(() => {});
      }
    } catch { /* unsupported */ }
  }, []);
  useEffect(() => {
    const unlock = () => {
      requestNotifPermission();
      // Unlock the alarm <audio> element (muted play/pause during the gesture),
      // so a later programmatic play() is allowed by iOS/Safari. Do this once.
      try {
        if (!alarmRef.current) { const a = new Audio(alarmDataUri()); a.preload = 'auto'; alarmRef.current = a; }
        if (!alarmUnlockedRef.current) {
          const a = alarmRef.current;
          a.muted = true; a.volume = 0;
          const done = () => { try { a.pause(); a.currentTime = 0; a.muted = false; a.volume = 1; } catch { /* noop */ } };
          const p = a.play();
          if (p && p.then) p.then(done).catch(() => { a.muted = false; a.volume = 1; });
          else done();
          alarmUnlockedRef.current = true;
        }
      } catch { /* noop */ }
      // Unlock / resume the Web Audio context.
      try {
        const Ctor = window.AudioContext || window.webkitAudioContext;
        if (Ctor) {
          if (!audioCtxRef.current) audioCtxRef.current = new Ctor();
          if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume().catch(() => {});
        }
      } catch { /* noop */ }
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    // Re-arm audio when returning to the tab.
    const onVis = () => { if (document.visibilityState === 'visible') { try { audioCtxRef.current?.resume?.(); } catch { /* noop */ } } };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [requestNotifPermission]);


  // Search suggestions — full-text (title + body + tags) across owned + shared,
  // fetched whenever the debounced query changes. Shows a loading indicator
  // while running and returns folder name + a match snippet per result.
  useEffect(() => {
    if (!debounced) { setNoteSug([]); setSearchLoading(false); return; }
    let cancel = false;
    setSearchLoading(true);
    (async () => {
      try {
        const p = new URLSearchParams();
        p.append('q', debounced);
        p.append('limit', '8');
        const { data } = await notesApi.get(`/notes/notes/search?${p.toString()}`);
        if (!cancel) setNoteSug(data || []);
      } catch { if (!cancel) setNoteSug([]); }
      finally { if (!cancel) setSearchLoading(false); }
    })();
    return () => { cancel = true; };
  }, [debounced]);

  // Cascading tags — compute which tags co-occur with the current selection so
  // the search dropdown only suggests tags that would actually return notes.
  useEffect(() => {
    if (!selectedTags.length) { setCoTagCounts(null); return; }
    let cancel = false;
    (async () => {
      try {
        const p = new URLSearchParams();
        p.append('include_shared', 'true');
        selectedTags.forEach((t) => p.append('tags', t));
        const { data } = await notesApi.get(`/notes/notes?${p.toString()}`);
        if (cancel) return;
        const counts = {};
        (data || []).forEach((n) => (n.tags || []).forEach((tg) => { counts[tg] = (counts[tg] || 0) + 1; }));
        setCoTagCounts(counts);
      } catch { if (!cancel) setCoTagCounts({}); }
    })();
    return () => { cancel = true; };
  }, [selectedTags]);

  // Close the search dropdown on outside click / Escape.
  useEffect(() => {
    const onDown = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setSearchOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, []);

  // Global keyboard: Command Palette (Cmd/Ctrl+K or "/") + module shortcuts
  // (g then n → Notes, g then t → Tasks). All typing-aware: never fire while
  // the user is editing text (inputs, textareas, or the TipTap contenteditable).
  const gPendingRef = useRef(null);
  useEffect(() => {
    const isTyping = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = (el.tagName || '').toLowerCase();
      return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
    };
    const onKey = (e) => {
      // Cmd/Ctrl+K — always toggles the palette (even from a field).
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setCmdkOpen((v) => !v);
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (cmdkOpen) return;           // palette handles its own keys while open
      if (isTyping()) return;         // don't hijack typing
      // "/" opens the palette.
      if (e.key === '/') { e.preventDefault(); setCmdkOpen(true); return; }
      // g-then-n / g-then-t module switch.
      const k = (e.key || '').toLowerCase();
      if (gPendingRef.current) {
        clearTimeout(gPendingRef.current);
        gPendingRef.current = null;
        if (k === 'n') { e.preventDefault(); navigate('/notes'); return; }
        if (k === 't') { e.preventDefault(); navigate('/notes/tasks'); return; }
        if (k === 'c') { if (!chatEnabledRef.current) return; e.preventDefault(); setChatOpen(true); return; }
      }
      if (k === 'g') {
        gPendingRef.current = setTimeout(() => { gPendingRef.current = null; }, 800);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); if (gPendingRef.current) clearTimeout(gPendingRef.current); };
  }, [cmdkOpen, navigate]);

  // Close reminders popover on outside click.
  useEffect(() => {
    const h = (e) => { if (remindersRef.current && !remindersRef.current.contains(e.target)) setRemindersOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // --- WebSocket plumbing ---------------------------------------------------
  const wsSend = useCallback((obj) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) { try { ws.send(JSON.stringify(obj)); } catch { /* ignore */ } }
  }, []);

  const subscribeNote = useCallback((id) => {
    const prev = subscribedRef.current;
    if (prev && prev !== id) wsSend({ type: 'unsubscribe', note_id: prev });
    if (id) wsSend({ type: 'subscribe', note_id: id });
    subscribedRef.current = id;
  }, [wsSend]);

  const markLocalSave = useCallback((id) => { if (id) localSaveRef.current[id] = Date.now(); }, []);

  const openNote = useCallback(async (id) => {
    setSelectedId(id);
    setNoteLoading(true);
    try {
      const { data } = await notesApi.get(`/notes/notes/${id}`);
      setSelectedNote(data);
      subscribeNote(id);
    } catch {
      notify('Could not open that note.');
      setSelectedNote(null);
    } finally {
      setNoteLoading(false);
    }
  }, [notify, subscribeNote]);
  useEffect(() => { openNoteRef.current = openNote; }, [openNote]);

  // ---- Web Push deep-linking ------------------------------------------------
  // A push notification click lands on /notes?note=<id>. Open that note, then
  // strip the param so it doesn't reopen on later renders.
  useEffect(() => {
    const noteId = searchParams.get('note');
    if (!noteId) return;
    openNote(noteId);
    const next = new URLSearchParams(searchParams);
    next.delete('note');
    setSearchParams(next, { replace: true });
  }, [searchParams, openNote, setSearchParams]);

  // The service worker asks the (already open) app to route in-place after a
  // notification click. Same-origin path only — router handles the rest.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const onMsg = (e) => {
      const d = e.data;
      if (d && d.type === 'inventique-push-navigate' && d.url) {
        try { navigate(d.url); } catch { /* ignore bad path */ }
      }
    };
    navigator.serviceWorker.addEventListener('message', onMsg);
    return () => navigator.serviceWorker.removeEventListener('message', onMsg);
  }, [navigate]);

  // ---- Triple-tap / triple-click opens the command palette ------------------
  // Three quick taps in the same spot (<500ms apart) open the palette — handy
  // on phones with no keyboard. Ignored while typing in a field.
  const tripleTapRef = useRef({ count: 0, last: 0, x: 0, y: 0 });
  useEffect(() => {
    const onTap = (e) => {
      const el = e.target;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)) return;
      const now = Date.now();
      const s = tripleTapRef.current;
      const near = Math.abs(e.clientX - s.x) < 40 && Math.abs(e.clientY - s.y) < 40;
      s.count = (now - s.last < 500 && near) ? s.count + 1 : 1;
      s.last = now; s.x = e.clientX; s.y = e.clientY;
      if (s.count >= 3) { s.count = 0; setCmdkOpen(true); }
    };
    window.addEventListener('click', onTap);
    return () => window.removeEventListener('click', onTap);
  }, []);

  // After restoring a version, refresh the open note + remount the editor.
  const handleVersionRestored = useCallback((updated) => {
    if (!updated) return;
    setSelectedNote(updated);
    setNotes((list) => list.map((x) => (x.id === updated.id
      ? { ...x, title: updated.title, snippet: updated.snippet, has_attachments: updated.has_attachments, updated_at: updated.updated_at }
      : x)));
    setRemoteReloadKey((k) => k + 1);
    notify('Version restored.');
  }, [notify]);

  const handleWsMessage = useCallback((msg) => {
    const t = msg?.type;
    if (t === 'note_updated' && msg.note) {
      const n = msg.note;
      // Keep my own note cards fresh live; shared-note cards refresh on the
      // 5-minute auto-refresh (or manual refresh) rather than live.
      setNotes((prev) => prev.map((x) => ((x.id === n.id && !x.shared_by)
        ? { ...x, title: n.title, snippet: n.snippet, tags: n.tags, is_favorite: n.is_favorite, has_attachments: n.has_attachments, reminder_at: n.reminder_at, updated_at: n.updated_at }
        : x)));
      if (n.id === selectedIdRef.current) {
        const cur = selectedNoteRef.current;
        const canEditLocal = cur?.permission ? cur.permission === 'edit' : true;
        const recent = localSaveRef.current[n.id];
        const isEcho = recent && (Date.now() - recent < 3000);
        if (isEcho) return; // my own change coming back — ignore
        if (!canEditLocal) {
          // Viewer → apply live and re-mount the editor with fresh content.
          setSelectedNote((prev) => ({ ...n, role: prev?.role, permission: prev?.permission, shared_by: prev?.shared_by }));
          setRemoteReloadKey((k) => k + 1);
        } else {
          // Owner / co-editor → don't clobber in-progress typing; offer a reload.
          notify('This note was just updated by a collaborator. Tap to reload.', () => {
            openNote(n.id); setRemoteReloadKey((k) => k + 1);
          });
        }
      }
    } else if (t === 'note_deleted') {
      const id = msg.note_id;
      setNotes((prev) => prev.filter((x) => x.id !== id));
      if (id === selectedIdRef.current) { setSelectedId(null); setSelectedNote(null); subscribedRef.current = null; notify('This note was deleted by its owner.'); }
    } else if (t === 'list_changed') {
      // Live list reloads are intentionally disabled — the notes list now
      // refreshes on the 5-minute auto-refresh (or the manual refresh button).
    } else if (t === 'shares_changed') {
      // Don't reload the list live; just surface a subtle hint. The next
      // auto-refresh (or manual refresh) will pull in the newly shared items.
      setSharedInbox((c) => c + 1);
      notify('Something was shared with you — it will appear on the next refresh.', () => { setSharedInbox(0); refreshAll(); setView('shared'); });
    } else if (t === 'presence' && msg.note_id) {
      setPresence((prev) => ({ ...prev, [msg.note_id]: msg.users || [] }));
    }
  }, [notify, openNote, refreshAll]);

  useEffect(() => { handlerRef.current = handleWsMessage; }, [handleWsMessage]);

  useEffect(() => {
    const token = localStorage.getItem('notes_token') || localStorage.getItem('inv_token');
    if (!token) return;
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http/i, 'ws');
    const url = `${base}/api/notes/ws?token=${encodeURIComponent(token)}`;
    let closed = false; let pingIv = null; let reconnectTimer = null;
    const connect = () => {
      let ws;
      try { ws = new WebSocket(url); } catch { return; }
      wsRef.current = ws;
      ws.onopen = () => {
        pingIv = setInterval(() => { try { if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' })); } catch { /* ignore */ } }, 25000);
        const sid = selectedIdRef.current;
        if (sid) { try { ws.send(JSON.stringify({ type: 'subscribe', note_id: sid })); } catch { /* ignore */ } subscribedRef.current = sid; }
      };
      ws.onmessage = (e) => { try { handlerRef.current?.(JSON.parse(e.data)); } catch { /* ignore */ } };
      ws.onclose = () => { if (pingIv) clearInterval(pingIv); if (!closed) reconnectTimer = setTimeout(connect, 2500); };
      ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
    };
    connect();
    return () => { closed = true; if (pingIv) clearInterval(pingIv); if (reconnectTimer) clearTimeout(reconnectTimer); try { wsRef.current?.close(); } catch { /* ignore */ } };
  }, []);

  const openShare = useCallback((resource) => setShareDialog({ open: true, resource }), []);

  const toggleReminders = useCallback(() => {
    setRemindersOpen((o) => {
      const nx = !o;
      if (nx) notesApi.get('/notes/notes/reminders').then(({ data }) => setUpcomingReminders(data || [])).catch(() => {});
      return nx;
    });
  }, []);

  const lightOf = (n) => ({
    id: n.id, title: n.title, snippet: n.snippet, tags: n.tags || [],
    is_favorite: n.is_favorite, reminder_at: n.reminder_at, has_attachments: n.has_attachments,
    folder_id: n.folder_id, created_at: n.created_at, updated_at: n.updated_at,
  });

  const createNote = useCallback(async (template) => {
    setTemplatePicker(false);
    try {
      const inSharedFolder = view.startsWith('shared:');
      const folderId = inSharedFolder ? view.slice(7)
        : (view !== 'all' && view !== 'favorites' && !view.startsWith('shared')) ? view : null;
      const body = {
        title: template?.title || '',
        body_html: template?.body_html || '',
        tags: template?.tags || [],
        template_id: template?.id || null,
        folder_id: folderId,
      };
      const { data } = await notesApi.post('/notes/notes', body);
      markLocalSave(data.id);
      setSelectedNote(data);
      setSelectedId(data.id);
      subscribeNote(data.id);
      setNotes((prev) => [lightOf(data), ...prev.filter((n) => n.id !== data.id)]);
      loadFolders();
      if (inSharedFolder) { loadSharedFolders(); loadNotes(); }
      if (template?.tags?.length) loadTags();
    } catch {
      notify('Could not create note.');
    }
  }, [view, loadFolders, loadSharedFolders, loadNotes, loadTags, notify, markLocalSave, subscribeNote]);

  const saveNote = useCallback(async (patch) => {
    if (!selectedId) return;
    try {
      markLocalSave(selectedId);
      const { data } = await notesApi.patch(`/notes/notes/${selectedId}`, patch);
      markLocalSave(selectedId);
      setSelectedNote((prev) => (prev ? { ...prev, ...data } : data));
      setNotes((prev) => prev.map((n) => (n.id === data.id
        ? { ...n, title: data.title, snippet: data.snippet, tags: data.tags, is_favorite: data.is_favorite, has_attachments: data.has_attachments, folder_id: data.folder_id, reminder_at: data.reminder_at, updated_at: data.updated_at }
        : n)));
      if ('tags' in patch) loadTags();
      if ('folder_id' in patch) { loadFolders(); loadNotes(); }
    } catch (e) {
      notify(e?.response?.data?.detail || 'Could not save changes.');
    }
  }, [selectedId, loadTags, loadFolders, loadNotes, notify, markLocalSave]);

  const toggleFavorite = useCallback((note) => saveNote({ is_favorite: !note.is_favorite }), [saveNote]);

  // Pin / unpin a note for me (personal). Optimistically flips the flag in the
  // list + open note, then refreshes the pinned counter; in the Pinned view a
  // successful unpin drops the note from the list.
  const togglePin = useCallback(async (note) => {
    if (!note?.id) return;
    const next = !note.pinned;
    setNotes((prev) => {
      if (view === 'pinned' && !next) return prev.filter((n) => n.id !== note.id);
      return prev.map((n) => (n.id === note.id ? { ...n, pinned: next } : n));
    });
    setSelectedNote((prev) => (prev && prev.id === note.id ? { ...prev, pinned: next } : prev));
    try {
      await notesApi.put(`/notes/notes/${note.id}/pin`, { pinned: next });
      setPinnedCount((c) => Math.max(0, c + (next ? 1 : -1)));
      notify(next ? 'Pinned to top.' : 'Unpinned.');
    } catch {
      // revert on failure
      setNotes((prev) => prev.map((n) => (n.id === note.id ? { ...n, pinned: !next } : n)));
      setSelectedNote((prev) => (prev && prev.id === note.id ? { ...prev, pinned: !next } : prev));
      if (view === 'pinned') loadNotes(true);
      notify('Could not update pin.');
    }
  }, [view, notify, loadNotes]);

  // Drag a note card onto a folder to file it there.
  const moveNoteToFolder = useCallback(async (noteId, folderId) => {
    try {
      markLocalSave(noteId);
      const { data } = await notesApi.patch(`/notes/notes/${noteId}`, { folder_id: folderId || '' });
      setNotes((prev) => prev.map((n) => (n.id === data.id ? { ...n, folder_id: data.folder_id, updated_at: data.updated_at } : n)));
      setSelectedNote((prev) => (prev && prev.id === noteId ? { ...prev, folder_id: data.folder_id } : prev));
      loadFolders();
      if (view !== 'all' && view !== 'favorites') loadNotes();
      const fname = folders.find((f) => f.id === folderId)?.name;
      notify(fname ? `Moved to “${fname}”.` : 'Moved to All Notes.');
    } catch { notify('Could not move note.'); }
  }, [view, folders, loadFolders, loadNotes, notify, markLocalSave]);

  // Attachments changed inside the editor → keep selected note + list badge in sync.
  const handleAttachmentsChanged = useCallback((atts) => {
    markLocalSave(selectedId);
    setSelectedNote((prev) => (prev ? { ...prev, attachments: atts } : prev));
    setNotes((prev) => prev.map((n) => (n.id === selectedId ? { ...n, has_attachments: (atts?.length || 0) > 0 } : n)));
  }, [selectedId, markLocalSave]);

  const askDeleteNote = useCallback((note) => {
    setConfirm({
      open: true, kind: 'note', title: 'Delete note?',
      message: `“${note.title || 'Untitled'}” will be permanently removed. This cannot be undone.`,
      danger: true, confirmLabel: 'Delete',
      onConfirm: async () => {
        try {
          await notesApi.delete(`/notes/notes/${note.id}`);
          setNotes((prev) => {
            const rest = prev.filter((n) => n.id !== note.id);
            if (selectedId === note.id) {
              const next = rest[0];
              if (next) openNote(next.id); else { setSelectedId(null); setSelectedNote(null); }
            }
            return rest;
          });
          loadFolders(); loadTags();
          notify('Note deleted.');
        } catch { notify('Could not delete note.'); }
        setConfirm({ open: false });
      },
    });
  }, [selectedId, openNote, loadFolders, loadTags, notify]);

  // Folder handlers
  const submitFolder = useCallback(async (name, color) => {
    try {
      if (folderDialog.initial) {
        await notesApi.patch(`/notes/folders/${folderDialog.initial.id}`, { name, color: color || '' });
      } else {
        await notesApi.post('/notes/folders', { name, color: color || '', parent_id: folderDialog.parentId || null });
      }
      setFolderDialog({ open: false, initial: null, parentId: null });
      loadFolders();
    } catch { notify('Could not save folder.'); }
  }, [folderDialog, loadFolders, notify]);

  const openAddSubfolder = useCallback((parent) => {
    setCollapsed((prev) => { const n = new Set(prev); n.delete(parent.id); return n; });
    setFolderDialog({ open: true, initial: null, parentId: parent.id });
  }, []);

  const askDeleteFolder = useCallback((folder) => {
    setConfirm({
      open: true, kind: 'folder', title: 'Delete folder?',
      message: `“${folder.name}” will be removed. Any subfolders move up one level and its notes return to All Notes.`,
      danger: true, confirmLabel: 'Delete folder',
      onConfirm: async () => {
        try {
          await notesApi.delete(`/notes/folders/${folder.id}`);
          if (view === folder.id) setView('all');
          loadFolders(); loadNotes();
          notify('Folder deleted.');
        } catch { notify('Could not delete folder.'); }
        setConfirm({ open: false });
      },
    });
  }, [view, loadFolders, loadNotes, notify]);

  // Tag handlers (standalone create + delete-with-cascade)
  const createTag = useCallback(async () => {
    const t = newTag.trim().replace(/^#/, '');
    if (!t) { setTagAdding(false); setNewTag(''); return; }
    try {
      await notesApi.post('/notes/tags', { tag: t });
      setNewTag(''); setTagAdding(false);
      loadTags();
    } catch (e) { notify(e?.response?.data?.detail || 'Could not create tag.'); }
  }, [newTag, loadTags, notify]);

  const askDeleteTag = useCallback((tag) => {
    setConfirm({
      open: true, kind: 'tag', title: 'Delete tag?',
      message: `“#${tag}” will be removed from every note. The notes themselves are kept.`,
      danger: true, confirmLabel: 'Delete tag',
      onConfirm: async () => {
        try {
          await notesApi.delete(`/notes/tags?tag=${encodeURIComponent(tag)}`);
          setSelectedTags((prev) => prev.filter((t) => t !== tag));
          loadTags(); loadNotes();
          if (selectedId) openNote(selectedId);
          notify('Tag deleted.');
        } catch { notify('Could not delete tag.'); }
        setConfirm({ open: false });
      },
    });
  }, [loadTags, loadNotes, selectedId, openNote, notify]);

  // Rename a tag everywhere it's used (cascades to all of the user's notes).
  const renameTag = useCallback(async (oldTag, nextRaw) => {
    const next = String(nextRaw || '').trim().replace(/^#/, '').trim();
    setTagEditing(null); setTagEditVal('');
    if (!next || next === oldTag) return;
    try {
      const { data } = await notesApi.patch('/notes/tags', { old: oldTag, new: next });
      const fin = data.tag || next;
      setSelectedTags((prev) => prev.map((t) => (t === oldTag ? fin : t)));
      await Promise.all([loadTags(), loadNotes()]);
      if (selectedId) openNote(selectedId);
      notify(`Tag renamed to “${fin}”${data.updated ? ` · updated ${data.updated} note${data.updated === 1 ? '' : 's'}` : ''}.`);
    } catch (e) { notify(e?.response?.data?.detail || 'Could not rename tag.'); }
  }, [loadTags, loadNotes, selectedId, openNote, notify]);

  const toggleTag = useCallback((tag) => {
    setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  }, []);

  // ── Pinned tag combos (favourites) ──────────────────────────────────────
  const comboKey = useCallback((arr) => [...(arr || [])].sort().join('\u0000'), []);
  const persistPinned = useCallback((next) => {
    setPinnedCombos(next);
    try { localStorage.setItem('notes:pinnedTagCombos', JSON.stringify(next)); } catch { /* noop */ }
  }, []);
  const currentKey = comboKey(selectedTags);
  const isCurrentPinned = useMemo(
    () => selectedTags.length > 0 && pinnedCombos.some((c) => comboKey(c.tags) === currentKey),
    [pinnedCombos, selectedTags, currentKey, comboKey],
  );
  const togglePinCurrent = useCallback(() => {
    if (!selectedTags.length) return;
    const key = comboKey(selectedTags);
    const existing = pinnedCombos.find((c) => comboKey(c.tags) === key);
    if (existing) {
      persistPinned(pinnedCombos.filter((c) => c.id !== existing.id));
    } else {
      const combo = { id: genId(), tags: [...selectedTags].sort(), label: '' };
      persistPinned([combo, ...pinnedCombos].slice(0, 20));
      // Immediately offer to name it (optional — leaving it blank falls back to chips).
      setEditingComboId(combo.id);
      setEditingLabel('');
    }
  }, [selectedTags, pinnedCombos, comboKey, persistPinned]);
  const applyCombo = useCallback((combo) => { setSelectedTags([...combo.tags]); }, []);
  const unpinCombo = useCallback((combo) => {
    persistPinned(pinnedCombos.filter((c) => c.id !== combo.id));
    setEditingComboId((cur) => (cur === combo.id ? null : cur));
  }, [pinnedCombos, persistPinned]);
  const startRenameCombo = useCallback((combo) => {
    setEditingComboId(combo.id);
    setEditingLabel(combo.label || '');
  }, []);
  const commitRenameCombo = useCallback(() => {
    if (!editingComboId) return;
    const label = editingLabel.trim().slice(0, 40);
    persistPinned(pinnedCombos.map((c) => (c.id === editingComboId ? { ...c, label } : c)));
    setEditingComboId(null);
    setEditingLabel('');
  }, [editingComboId, editingLabel, pinnedCombos, persistPinned]);

  const isDark = theme === 'dark';
  const initials = (principal?.name || principal?.email || '?').slice(0, 1).toUpperCase();
  const noteIsOwned = !selectedNote?.role || selectedNote?.role === 'owner';

  // Interactive controls shared by tree + flat (color-filtered) folder rows.
  const FolderActions = ({ f }) => (
    <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
      <button onClick={() => openShare({ type: 'folder', id: f.id, name: f.name })} title="Share folder" data-testid="notes-folder-share"
        className="p-1 text-[var(--n-muted-2)] hover:text-[var(--n-accent)]"><Users2 size={13} /></button>
      <button onClick={() => openAddSubfolder(f)} title="New subfolder" data-testid="notes-folder-add-sub"
        className="p-1 text-[var(--n-muted-2)] hover:text-[var(--n-accent)]"><FolderPlus size={13} /></button>
      <button onClick={() => setFolderDialog({ open: true, initial: f, parentId: null })} title="Rename" data-testid="notes-folder-rename"
        className="p-1 text-[var(--n-muted-2)] hover:text-[var(--n-ink)]"><Pencil size={13} /></button>
      <button onClick={() => askDeleteFolder(f)} title="Delete" data-testid="notes-folder-delete"
        className="p-1 text-[var(--n-muted-2)] hover:text-[var(--n-danger)]"><Trash2 size={13} /></button>
    </div>
  );

  // Recursive folder tree renderer.
  const renderFolderNodes = (parentId, depth) => {
    const kids = childrenMap[parentId] || [];
    return kids.map((f) => {
      const grand = childrenMap[f.id] || [];
      const isOpen = !collapsed.has(f.id);
      const active = view === f.id;
      return (
        <div key={f.id}>
          <div className="group flex items-center gap-0.5" style={{ paddingLeft: depth * 12 }}>
            {grand.length ? (
              <button onClick={() => toggleCollapse(f.id)} title={isOpen ? 'Collapse' : 'Expand'} data-testid="notes-folder-toggle"
                className="n-focus shrink-0 p-1 text-[var(--n-muted-2)] hover:text-[var(--n-ink)]">
                <ChevronRight size={13} className={`transition-transform ${isOpen ? 'rotate-90' : ''}`} />
              </button>
            ) : <span className="w-[21px] shrink-0" aria-hidden="true" />}
            <button onClick={() => setView(f.id)} data-testid="notes-folder-item"
              onDragOver={(e) => { e.preventDefault(); setDragOverFolder(f.id); }}
              onDragLeave={() => setDragOverFolder((d) => (d === f.id ? null : d))}
              onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData('text/note-id'); setDragOverFolder(null); if (id) moveNoteToFolder(id, f.id); }}
              className={`n-focus flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2 py-2 text-sm transition-colors
                ${dragOverFolder === f.id ? 'ring-2 ring-[var(--n-accent)] bg-[var(--n-accent-bg)]' : ''}
                ${active ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)] font-semibold' : 'text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
              <Folder size={15} strokeWidth={1.8} className="shrink-0" style={f.color ? { color: f.color } : undefined} />
              <span className="flex-1 truncate text-left">{f.name}</span>
              {f.is_shared && <Share2 size={11} className="shrink-0 text-[var(--n-accent)]" aria-label="Shared folder" data-testid="notes-folder-shared-indicator" />}
              <span className="text-[11px] text-[var(--n-muted-2)] tabular-nums">{f.count}</span>
            </button>
            <FolderActions f={f} />
          </div>
          {grand.length > 0 && isOpen && renderFolderNodes(f.id, depth + 1)}
        </div>
      );
    });
  };

  // Recursive shared-folder tree renderer (read-only; no drag/move/actions).
  // Uses `shared:<id>` views and shows the inherited permission per node.
  const renderSharedFolderNodes = (parentId, depth) => {
    const kids = sharedChildrenMap[parentId] || [];
    return kids.map((f) => {
      const grand = sharedChildrenMap[f.id] || [];
      const isOpen = !collapsed.has(f.id);
      const active = view === `shared:${f.id}`;
      return (
        <div key={f.id}>
          <div className="group flex items-center gap-0.5" style={{ paddingLeft: depth * 12 }}>
            {grand.length ? (
              <button onClick={() => toggleCollapse(f.id)} title={isOpen ? 'Collapse' : 'Expand'} data-testid="notes-shared-folder-toggle"
                className="n-focus shrink-0 p-1 text-[var(--n-muted-2)] hover:text-[var(--n-ink)]">
                <ChevronRight size={13} className={`transition-transform ${isOpen ? 'rotate-90' : ''}`} />
              </button>
            ) : <span className="w-[21px] shrink-0" aria-hidden="true" />}
            <button onClick={() => setView(`shared:${f.id}`)} data-testid="notes-shared-folder-item"
              className={`n-focus flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2 py-2 text-sm transition-colors
                ${active ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)] font-semibold' : 'text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
              <FolderOpen size={15} strokeWidth={1.8} className="shrink-0" style={f.color ? { color: f.color } : undefined} />
              <span className="flex-1 truncate text-left">{f.name}</span>
              <span title={f.permission === 'edit' ? 'Can edit' : 'View only'} className="shrink-0 text-[var(--n-muted-2)]">
                {f.permission === 'edit' ? <Pencil size={12} /> : <Eye size={12} />}
              </span>
            </button>
          </div>
          {grand.length > 0 && isOpen && renderSharedFolderNodes(f.id, depth + 1)}
        </div>
      );
    });
  };

  // A single tag chip (used by both the "my tags" group and the "Shared" group).
  const renderTagChip = ({ tag, count, shared_only }) => {
    const on = selectedTags.includes(tag);
    if (tagEditing === tag && !shared_only) {
      return (
        <input key={tag} autoFocus value={tagEditVal}
          onChange={(e) => setTagEditVal(e.target.value)}
          onBlur={() => renameTag(tag, tagEditVal)}
          onKeyDown={(e) => { if (e.key === 'Enter') renameTag(tag, tagEditVal); else if (e.key === 'Escape') { setTagEditing(null); setTagEditVal(''); } }}
          data-testid="notes-tag-rename-input" maxLength={48}
          className="n-focus w-28 rounded-full border border-[var(--n-accent)] bg-[var(--n-surface)] px-2.5 py-1 text-[12px] font-medium text-[var(--n-ink)]" />
      );
    }
    return (
      <span key={tag} data-testid="notes-tag-chip"
        className={`group inline-flex items-center gap-1 rounded-full border pl-2.5 pr-1 py-1 text-[12px] font-medium transition-colors
          ${on ? 'border-[var(--n-accent)] bg-[var(--n-accent)] text-white' : 'border-[var(--n-border)] text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
        <button onClick={() => toggleTag(tag)} className="n-focus inline-flex items-center gap-1" data-testid="notes-tag-toggle">
          <Hash size={11} />{tag}
          <span className={`text-[10px] ${on ? 'text-white/80' : 'text-[var(--n-muted-2)]'}`}>{count}</span>
        </button>
        <button onClick={(e) => { e.stopPropagation(); setTagEditing(tag); setTagEditVal(tag); }} title={`Rename tag ${tag}`} aria-label={`Rename tag ${tag}`}
          data-testid="notes-tag-rename"
          className={`${shared_only ? 'hidden' : 'inline-flex'} h-4 w-4 items-center justify-center rounded-full opacity-100 transition-opacity
            ${on ? 'hover:bg-white/20 text-white' : 'text-[var(--n-muted)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-ink)]'}`}>
          <Pencil size={11} />
        </button>
        <button onClick={(e) => { e.stopPropagation(); askDeleteTag(tag); }} title="Delete tag" aria-label={`Delete tag ${tag}`}
          data-testid="notes-tag-delete"
          className={`${shared_only ? 'hidden' : 'inline-flex'} h-4 w-4 items-center justify-center rounded-full opacity-60 group-hover:opacity-100 transition-opacity
            ${on ? 'hover:bg-white/20 text-white' : 'hover:bg-[var(--n-danger)]/10 text-[var(--n-muted-2)] hover:text-[var(--n-danger)]'}`}>
          <X size={11} />
        </button>
      </span>
    );
  };

  return (
    <div className={`notes-scope ${isDark ? 'dark' : ''} h-screen w-full flex flex-col overflow-hidden`}
      style={{ background: 'var(--n-canvas)', paddingRight: chatReserve || undefined, transition: 'padding-right 160ms ease' }} data-testid="notes-app-shell">

      {/* ---- Top bar ---- */}
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-[var(--n-line)] bg-[var(--n-surface)] px-3 sm:gap-3 md:px-5">
        {/* Mobile: open folders/tags drawer */}
        <button onClick={() => setMobileRailOpen(true)} title="Menu" aria-label="Open menu" data-testid="notes-mobile-menu-toggle"
          className="n-focus -ml-1 inline-flex md:hidden h-9 w-9 items-center justify-center rounded-lg text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)] transition-colors">
          <Menu size={20} />
        </button>
        <button onClick={toggleRail} title={railHidden ? 'Show panel' : 'Hide panel'} data-testid="notes-rail-toggle"
          className="n-focus -ml-1 hidden md:inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)] transition-colors">
          {railHidden ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        </button>
        <div className="flex items-center gap-2">
          <Feather size={20} className="text-[var(--n-accent)]" />
          <span className="font-display text-xl tracking-wide text-[var(--n-ink)] hidden sm:inline">Notes</span>
        </div>
        <div className="ml-1 sm:ml-3"><ModuleSwitch /></div>

        <div className="ml-auto flex items-center gap-1 sm:gap-1.5">
          <div className="relative" ref={remindersRef}>
            <button onClick={toggleReminders} title="Upcoming reminders" data-testid="notes-reminders-button"
              className="n-focus inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)] transition-colors">
              <Bell size={17} />
            </button>
            {remindersOpen && (
              <div className="fixed right-3 top-[calc(env(safe-area-inset-top,0px)+3.75rem)] z-[120] w-80 max-w-[calc(100vw-1.5rem)] rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-2 shadow-[var(--n-shadow)] sm:absolute sm:right-0 sm:top-auto sm:mt-1" data-testid="notes-reminders-panel">
                <div className="px-2 pb-2 pt-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">Upcoming reminders</div>
                {upcomingReminders.length === 0 ? (
                  <div className="px-2.5 py-5 text-center text-[12px] text-[var(--n-muted)]" data-testid="notes-reminders-empty">No reminders set.</div>
                ) : (
                  <div className="max-h-80 overflow-y-auto n-scroll">
                    {upcomingReminders.map((n) => {
                      const overdue = new Date(n.reminder_at).getTime() <= Date.now();
                      return (
                        <button key={n.id} onClick={() => { openNote(n.id); setRemindersOpen(false); }} data-testid="notes-reminder-item"
                          className="flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-[var(--n-canvas-2)]">
                          <Clock size={14} className={`mt-0.5 shrink-0 ${overdue ? 'text-[var(--n-danger)]' : 'text-[var(--n-accent)]'}`} />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[13px] font-medium text-[var(--n-ink)]">{n.title || 'Untitled'}</span>
                            <span className={`block text-[11px] ${overdue ? 'text-[var(--n-danger)]' : 'text-[var(--n-muted)]'}`}>{overdue ? 'Overdue · ' : ''}{formatWhen(n.reminder_at)}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
                {notifPerm !== 'granted' && notifPerm !== 'unsupported' && (
                  <div className="mt-1 rounded-lg border border-[var(--n-border)] bg-[var(--n-canvas-2)] p-2.5" data-testid="notes-reminder-permission">
                    <div className="flex items-start gap-2">
                      <BellRing size={14} className="mt-0.5 shrink-0 text-[var(--n-accent)]" />
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] leading-snug text-[var(--n-ink-soft)]">
                          {notifPerm === 'denied'
                            ? 'Notifications are blocked. Enable them for this site in your browser settings so reminders can alert you in other tabs.'
                            : 'Turn on notifications so reminders pop up and chime even when this tab is in the background.'}
                        </p>
                        {notifPerm === 'default' && (
                          <button onClick={requestNotifPermission} data-testid="notes-reminder-enable-alerts"
                            className="n-focus mt-1.5 inline-flex items-center gap-1.5 rounded-lg bg-[var(--n-accent)] px-2.5 py-1 text-[11px] font-semibold text-white hover:opacity-90">
                            Enable alerts
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {chatEnabled && chatReserve === 0 && (
            <button onClick={() => setChatOpen(true)} title="Chat" aria-label="Chat" data-testid="notes-chat-toggle"
              className="relative n-focus inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">
              <MessageSquare size={18} />
              {chatUnread > 0 && (
                <span data-testid="sidebar-chat-unread-badge" className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full px-1 text-[9px] font-bold text-white" style={{ background: 'var(--n-danger)' }}>{chatUnread > 99 ? '99+' : chatUnread}</span>
              )}
            </button>
          )}

          <PushBell onToast={notify} />

          <button onClick={toggleFocus} title={focusMode ? 'Exit focus mode' : 'Focus mode'} data-testid="notes-focus-toggle"
            className={`n-focus hidden sm:inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors
              ${focusMode ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
            {focusMode ? <Minimize2 size={17} /> : <Maximize2 size={17} />}
          </button>
          <button onClick={toggleTheme} title={isDark ? 'Switch to light (Editorial Gold)' : 'Switch to dark (Nocturne)'}
            data-testid="notes-theme-toggle"
            className="n-focus inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)] transition-colors">
            {isDark ? <Sun size={17} /> : <Moon size={17} />}
          </button>
          <button onClick={() => setTemplateManager(true)} data-testid="notes-templates-button"
            className="n-focus hidden sm:inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)] transition-colors">
            <FileText size={16} /> Templates
          </button>
          {isSuper && (
            <button onClick={() => setAdminUsers(true)} data-testid="notes-admin-users-button"
              className="n-focus hidden sm:inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)] transition-colors">
              <Users size={16} /> Users
            </button>
          )}

          <span className="mx-1 hidden h-6 w-px bg-[var(--n-border)] sm:block" />

          {isSuper && (
            <button onClick={() => navigate('/dashboard')} data-testid="notes-back-to-inventique"
              className="n-focus inline-flex items-center gap-1.5 rounded-lg border border-[var(--n-border)] px-3 py-2 text-sm font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)] transition-colors">
              <ArrowLeft size={15} /> <span className="hidden md:inline">Neutral Tag Domain</span>
            </button>
          )}

          <div className="flex items-center gap-2 pl-1">
            <div className="hidden h-8 w-8 items-center justify-center rounded-full bg-[var(--n-accent-bg)] text-xs font-bold text-[var(--n-accent)] sm:flex" title={principal?.email}>
              {initials}
            </div>
            <button onClick={logout} title="Sign out" data-testid="notes-logout-button"
              className="n-focus inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--n-muted)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-ink)] transition-colors">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      {/* ---- 3-column body ---- */}
      <div className="flex min-h-0 flex-1">

        {/* Left rail — folders + tags */}
        {!focusMode && (
        <>
        {/* Mobile drawer backdrop */}
        <div aria-hidden="true" onClick={() => setMobileRailOpen(false)}
          className={`fixed inset-0 z-[150] bg-black/40 md:hidden transition-opacity duration-200 ${mobileRailOpen ? 'opacity-100' : 'pointer-events-none opacity-0'}`} data-testid="notes-rail-backdrop" />

        {/* Desktop icon rail (ERP / Tasks-style) — shown when the panel is
            collapsed so primary navigation stays reachable instead of vanishing. */}
        {railHidden && (
          <aside data-testid="notes-sidebar-rail"
            className="hidden md:flex w-[60px] shrink-0 flex-col items-center gap-1.5 border-r border-[var(--n-line)] bg-[var(--n-canvas)] py-3">
            <button onClick={toggleRail} title="Expand panel" aria-label="Expand panel" data-testid="notes-rail-expand"
              className="n-focus mb-1 grid h-10 w-10 place-items-center rounded-xl text-[var(--n-ink-soft)] transition-colors hover:bg-[var(--n-canvas-2)]"><PanelLeftOpen size={18} /></button>
            <button onClick={() => setTemplatePicker(true)} title="New note" aria-label="New note" data-testid="notes-rail-new"
              className="n-focus grid h-10 w-10 place-items-center rounded-xl bg-[var(--n-accent)] text-white transition-opacity hover:opacity-90"><Plus size={18} /></button>
            <div className="my-1 h-px w-7 bg-[var(--n-line)]" />
            {[
              { icon: Layers, label: 'All Notes', id: 'all', count: totalNotes },
              { icon: Star, label: 'Favorites', id: 'favorites', count: favoriteCount },
              { icon: CalendarClock, label: 'Updated Today', id: 'updated_today', count: updatedTodayCount },
              { icon: Pin, label: 'Pinned Notes', id: 'pinned', count: pinnedCount },
              { icon: Inbox, label: 'All shared', id: 'shared', badge: sharedInbox, onClick: () => { setSharedInbox(0); setView('shared'); } },
            ].map((it) => {
              const isActive = view === it.id;
              return (
                <button key={it.id} title={it.count != null ? `${it.label} (${it.count})` : it.label} aria-label={it.label}
                  data-testid={`notes-rail-${it.id}`}
                  onClick={it.onClick || (() => setView(it.id))}
                  className={`n-focus relative grid h-10 w-10 place-items-center rounded-xl transition-colors ${isActive ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
                  <it.icon size={18} />
                  {it.badge != null && it.badge > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-[16px] place-items-center rounded-full bg-[var(--n-danger)] px-1 text-[9.5px] font-semibold tabular-nums text-white">{it.badge}</span>
                  )}
                </button>
              );
            })}
            {/* Folders peek — a temporary floating flyout so folders stay reachable
                while the panel is collapsed. Selecting a folder navigates + closes;
                clicking anywhere else dismisses it. Panel stays collapsed underneath. */}
            <div className="my-1 h-px w-7 bg-[var(--n-line)]" />
            <div className="relative">
              <button onClick={() => setFolderPeekOpen((v) => !v)} title="Folders" aria-label="Folders"
                data-testid="notes-rail-folders-peek-toggle" aria-expanded={folderPeekOpen} aria-haspopup="menu"
                className={`n-focus relative grid h-10 w-10 place-items-center rounded-xl transition-colors ${folderPeekOpen ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
                <Folder size={18} />
              </button>
              {folderPeekOpen && (
                <>
                  {/* Transparent click-away layer */}
                  <div className="fixed inset-0 z-[158]" aria-hidden="true" data-testid="notes-rail-folders-peek-backdrop"
                    onClick={() => setFolderPeekOpen(false)} />
                  <div role="menu" data-testid="notes-rail-folders-peek"
                    className="absolute left-full top-0 z-[159] ml-2 w-64 max-h-[70vh] overflow-y-auto n-scroll rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-2 shadow-[var(--n-shadow)]">
                    <div className="mb-1 flex items-center justify-between px-1.5">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">Folders</span>
                      <button onClick={() => { setFolderPeekOpen(false); setFolderDialog({ open: true, initial: null, parentId: null }); }}
                        data-testid="notes-rail-folders-peek-add" title="New folder"
                        className="n-focus text-[var(--n-muted)] hover:text-[var(--n-accent)]"><FolderPlus size={14} /></button>
                    </div>
                    {folders.length === 0 ? (
                      <p className="px-1.5 py-2 text-[12px] text-[var(--n-muted-2)]">No folders yet.</p>
                    ) : (
                      <div className="space-y-0.5">
                        {flattenFolderTree(folders).map((f) => (
                          <button key={f.id} role="menuitem" data-testid="notes-rail-folders-peek-item"
                            onClick={() => { setView(f.id); setFolderPeekOpen(false); }}
                            style={{ paddingLeft: 8 + f.depth * 12 }}
                            className={`n-focus flex w-full min-w-0 items-center gap-2 rounded-lg py-1.5 pr-2 text-sm transition-colors ${view === f.id ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)] font-semibold' : 'text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
                            <Folder size={14} strokeWidth={1.8} className="shrink-0" style={f.color ? { color: f.color } : undefined} />
                            <span className="min-w-0 flex-1 truncate text-left">{f.name}</span>
                            {f.is_shared && <Share2 size={11} className="shrink-0 text-[var(--n-accent)]" aria-label="Shared folder" />}
                            <span className="shrink-0 text-[11px] text-[var(--n-muted-2)] tabular-nums">{f.count}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </aside>
        )}
        <aside className={`fixed inset-y-0 left-0 z-[160] flex w-[280px] max-w-[85vw] shrink-0 flex-col border-r border-[var(--n-line)] bg-[var(--n-surface)] shadow-[var(--n-shadow)] transition-transform duration-200 ease-out
          ${mobileRailOpen ? 'translate-x-0' : '-translate-x-full'}
          md:static md:z-auto md:w-[272px] md:max-w-none md:translate-x-0 md:shadow-none md:transition-none ${railHidden ? 'md:hidden' : 'md:flex'}`} data-testid="notes-rail">
          {/* Mobile drawer header */}
          <div className="flex items-center justify-between border-b border-[var(--n-line)] px-3 py-2.5 md:hidden">
            <span className="font-display text-lg text-[var(--n-ink)]">Menu</span>
            <button onClick={() => setMobileRailOpen(false)} data-testid="notes-rail-close" aria-label="Close menu"
              className="n-focus inline-flex h-8 w-8 items-center justify-center rounded-lg text-[var(--n-muted)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-ink)]"><X size={18} /></button>
          </div>
          <div className="p-3">
            <button onClick={() => setTemplatePicker(true)} data-testid="notes-new-note-button"
              className="n-focus flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--n-accent)] px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 transition-opacity">
              <Plus size={17} /> New note
            </button>
          </div>

          <div className="flex-1 overflow-y-auto n-scroll px-3 pb-4">
            <div className="space-y-0.5">
              <RailItem icon={Layers} label="All Notes" count={totalNotes} active={view === 'all'} onClick={() => setView('all')} testid="notes-view-all" />
              <RailItem icon={Star} label="Favorites" count={favoriteCount} active={view === 'favorites'} onClick={() => setView('favorites')} testid="notes-view-favorites" />
              <RailItem icon={CalendarClock} label="Updated Today" count={updatedTodayCount} active={view === 'updated_today'} onClick={() => setView('updated_today')} testid="notes-view-updated-today" />
              <RailItem icon={Pin} label="Pinned Notes" count={pinnedCount} active={view === 'pinned'} onClick={() => setView('pinned')} testid="notes-view-pinned" />
            </div>

            {/* Folders */}
            <div className="mt-5 flex items-center justify-between px-2.5">
              <button type="button" onClick={() => toggleRailSec('folders')} data-testid="notes-sec-folders-toggle"
                className="n-focus flex items-center gap-1 rounded-md py-0.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)] hover:text-[var(--n-ink-soft)]">
                <ChevronRight size={13} className={`transition-transform ${railSec.folders ? 'rotate-90' : ''}`} />
                Folders
              </button>
              <button onClick={() => setFolderDialog({ open: true, initial: null, parentId: null })} data-testid="notes-add-folder-button"
                className="n-focus text-[var(--n-muted)] hover:text-[var(--n-accent)]" title="New folder">
                <FolderPlus size={15} />
              </button>
            </div>
            {railSec.folders && (<>
            {/* Folder color filter */}
            {usedColors.length > 0 && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5 px-2.5" data-testid="notes-folder-color-filter">
                {usedColors.map((c) => {
                  const active = colorFilter === c;
                  return (
                    <button key={c} type="button" onClick={() => setColorFilter((cur) => (cur === c ? null : c))} data-testid="notes-folder-color-filter-swatch"
                      title="Filter folders by colour"
                      className={`h-5 w-5 rounded-full ring-2 ring-offset-2 ring-offset-[var(--n-surface)] transition-transform ${active ? 'ring-[var(--n-accent)] scale-110' : 'ring-transparent hover:scale-105'}`}
                      style={{ background: c }} />
                  );
                })}
                {colorFilter && (
                  <button onClick={() => setColorFilter(null)} data-testid="notes-folder-color-filter-clear"
                    className="n-focus text-[11px] font-medium text-[var(--n-accent)] hover:underline">Clear</button>
                )}
              </div>
            )}
            <div className="mt-1.5 space-y-0.5">
              {colorFilter ? (
                colorFilteredFolders.length === 0 ? (
                  <p className="px-2.5 py-1 text-[12px] text-[var(--n-muted-2)]">No folders with this colour.</p>
                ) : colorFilteredFolders.map((f) => (
                  <div key={f.id} className="group flex items-center gap-0.5">
                    <span className="w-[21px] shrink-0" aria-hidden="true" />
                    <button onClick={() => setView(f.id)} data-testid="notes-folder-item"
                      onDragOver={(e) => { e.preventDefault(); setDragOverFolder(f.id); }}
                      onDragLeave={() => setDragOverFolder((d) => (d === f.id ? null : d))}
                      onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData('text/note-id'); setDragOverFolder(null); if (id) moveNoteToFolder(id, f.id); }}
                      className={`n-focus flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2 py-2 text-sm transition-colors
                        ${dragOverFolder === f.id ? 'ring-2 ring-[var(--n-accent)] bg-[var(--n-accent-bg)]' : ''}
                        ${view === f.id ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)] font-semibold' : 'text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
                      <Folder size={15} strokeWidth={1.8} className="shrink-0" style={f.color ? { color: f.color } : undefined} />
                      <span className="flex-1 truncate text-left">{f.name}</span>
                      <span className="text-[11px] text-[var(--n-muted-2)] tabular-nums">{f.count}</span>
                    </button>
                    <FolderActions f={f} />
                  </div>
                ))
              ) : (
                folders.length === 0 ? (
                  <p className="px-2.5 py-1 text-[12px] text-[var(--n-muted-2)]">No folders yet.</p>
                ) : renderFolderNodes('__root', 0)
              )}
            </div>
            </>)}

            {/* Shared with me */}
            <div className="mt-5 flex items-center justify-between px-2.5">
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">Shared with me</span>
            </div>
            <div className="mt-1.5 space-y-0.5">
              <RailItem icon={Inbox} label="All shared" active={view === 'shared'} badge={sharedInbox}
                onClick={() => { setSharedInbox(0); setView('shared'); }} testid="notes-view-shared" />
              {renderSharedFolderNodes('__root', 0)}
              {sharedFolders.length === 0 && (
                <p className="px-2.5 py-1 text-[12px] text-[var(--n-muted-2)]">Folders shared with you appear here.</p>
              )}
            </div>

            {/* Tags */}
            <div className="mt-5 flex items-center justify-between px-2.5">
              <button type="button" onClick={() => toggleRailSec('tags')} data-testid="notes-sec-tags-toggle"
                className="n-focus flex items-center gap-1 rounded-md py-0.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)] hover:text-[var(--n-ink-soft)]">
                <ChevronRight size={13} className={`transition-transform ${railSec.tags ? 'rotate-90' : ''}`} />
                Tags
              </button>
              <div className="flex items-center gap-2">
                {selectedTags.length > 0 && (
                  <button onClick={() => setSelectedTags([])} className="n-focus text-[11px] font-medium text-[var(--n-accent)] hover:underline" data-testid="notes-clear-tags">Clear</button>
                )}
                <button onClick={toggleTagSort} data-testid="notes-tag-sort-toggle" title={tagSort === 'az' ? 'Sorted A→Z (click for Z→A)' : 'Sorted Z→A (click for A→Z)'}
                  className="n-focus text-[var(--n-muted)] hover:text-[var(--n-accent)]">
                  {tagSort === 'az' ? <ArrowDownAZ size={15} /> : <ArrowUpAZ size={15} />}
                </button>
                <button onClick={() => { setTagAdding((v) => !v); setNewTag(''); if (!railSec.tags) toggleRailSec('tags'); }} data-testid="notes-add-tag-button"
                  className="n-focus text-[var(--n-muted)] hover:text-[var(--n-accent)]" title="New tag">
                  <Plus size={15} />
                </button>
              </div>
            </div>
            {railSec.tags && (<>
            {pinnedCombos.length > 0 && (
              <div className="mt-2 px-1" data-testid="notes-pinned-combos">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">
                    <Bookmark size={10} /> Pinned
                  </span>
                  <span className="h-px flex-1 bg-[var(--n-line)]" aria-hidden="true" />
                </div>
                <div className="flex flex-col gap-1">
                  {pinnedCombos.map((combo) => {
                    const active = comboKey(combo.tags) === currentKey;
                    const isEditing = editingComboId === combo.id;
                    return (
                      <div key={combo.id} data-testid="notes-pinned-combo"
                        className={`group flex items-center gap-1 rounded-lg border px-2 py-1 transition-colors ${active ? 'border-[var(--n-accent)] bg-[var(--n-accent-bg)]' : 'border-[var(--n-border)] hover:bg-[var(--n-canvas-2)]'}`}>
                        {isEditing ? (
                          <input autoFocus value={editingLabel} onChange={(e) => setEditingLabel(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') commitRenameCombo(); else if (e.key === 'Escape') { setEditingComboId(null); setEditingLabel(''); } }}
                            onBlur={commitRenameCombo} placeholder="Name this combo…" data-testid="notes-pinned-combo-label-input"
                            className="n-focus min-w-0 flex-1 rounded border border-[var(--n-border)] bg-[var(--n-surface)] px-1.5 py-0.5 text-[12px] text-[var(--n-ink)] placeholder:text-[var(--n-muted-2)]" />
                        ) : (
                          <button type="button" onClick={() => applyCombo(combo)} data-testid="notes-pinned-combo-apply"
                            title={`Apply: ${combo.tags.join(' + ')}`}
                            className="n-focus flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left">
                            {combo.label ? (
                              <>
                                <span className="truncate text-[12.5px] font-semibold text-[var(--n-ink)]" data-testid="notes-pinned-combo-name">{combo.label}</span>
                                <span className="flex flex-wrap items-center gap-0.5 text-[10px] text-[var(--n-muted-2)]">
                                  {combo.tags.map((t, i) => (
                                    <React.Fragment key={t}>{i > 0 && <span>+</span>}<span>#{t}</span></React.Fragment>
                                  ))}
                                </span>
                              </>
                            ) : (
                              <span className="flex flex-wrap items-center gap-1">
                                {combo.tags.map((t, i) => (
                                  <React.Fragment key={t}>
                                    {i > 0 && <span className="text-[9px] font-semibold uppercase text-[var(--n-muted-2)]">and</span>}
                                    <span className="inline-flex items-center gap-0.5 text-[11.5px] font-medium text-[var(--n-ink-soft)]"><Hash size={9} />{t}</span>
                                  </React.Fragment>
                                ))}
                              </span>
                            )}
                          </button>
                        )}
                        {!isEditing && (
                          <button type="button" onClick={() => startRenameCombo(combo)} title="Rename" aria-label="Rename tag combination" data-testid="notes-pinned-combo-rename"
                            className="n-focus grid h-5 w-5 shrink-0 place-items-center rounded text-[var(--n-muted-2)] opacity-0 transition-opacity group-hover:opacity-100 hover:text-[var(--n-accent)]"><Pencil size={11} /></button>
                        )}
                        <button type="button" onClick={() => unpinCombo(combo)} title="Unpin" aria-label="Unpin tag combination" data-testid="notes-pinned-combo-remove"
                          className="n-focus grid h-5 w-5 shrink-0 place-items-center rounded text-[var(--n-muted-2)] opacity-0 transition-opacity group-hover:opacity-100 hover:text-[var(--n-danger)]"><X size={12} /></button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {tagAdding && (
              <div className="mt-2 px-1">
                <div className="relative">
                  <Hash size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--n-muted-2)]" />
                  <input autoFocus value={newTag} onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') createTag(); else if (e.key === 'Escape') { setTagAdding(false); setNewTag(''); } }}
                    onBlur={() => { if (!newTag.trim()) setTagAdding(false); }}
                    placeholder="New tag name…" data-testid="notes-new-tag-input"
                    className="n-focus w-full rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] pl-7 pr-3 py-1.5 text-[13px] text-[var(--n-ink)] placeholder:text-[var(--n-muted-2)]" />
                </div>
              </div>
            )}
            {/* Tag search — filters the list (and cascade candidates). */}
            {tags.length > 0 && (
              <div className="mt-2 px-1">
                <div className="relative">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--n-muted-2)]" />
                  <input value={tagQuery} onChange={(e) => setTagQuery(e.target.value)}
                    placeholder={cascadeActive ? 'Search matching tags…' : 'Search tags…'} data-testid="notes-tag-search"
                    className="n-focus w-full rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] pl-7 pr-7 py-1.5 text-[13px] text-[var(--n-ink)] placeholder:text-[var(--n-muted-2)]" />
                  {tagQuery && (
                    <button onClick={() => setTagQuery('')} aria-label="Clear tag search" data-testid="notes-tag-search-clear"
                      className="n-focus absolute right-2 top-1/2 -translate-y-1/2 text-[var(--n-muted-2)] hover:text-[var(--n-ink)]"><X size={12} /></button>
                  )}
                </div>
              </div>
            )}
            <div className="mt-1.5 px-1">
              {tags.length === 0 && !tagAdding && <p className="px-1.5 py-1 text-[12px] text-[var(--n-muted-2)]">No tags yet.</p>}
              {(() => {
                const q = tagQuery.trim().toLowerCase();
                const matchQ = (t) => !q || t.tag.toLowerCase().includes(q);
                if (cascadeActive) {
                  // Cascade mode: selected tags (removable) + only co-occurring tags with counts.
                  const cands = cascadeTags.filter(matchQ);
                  return (
                    <>
                      <div className="flex flex-wrap gap-1.5" data-testid="notes-tags-selected-group">
                        {selectedTagChips.map(renderTagChip)}
                      </div>
                      <div className="mt-3 mb-1.5 flex items-center gap-2" data-testid="notes-tags-cascade-divider">
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">
                          Combine with{cands.length ? ` · ${cands.length}` : ''}
                        </span>
                        <span className="h-px flex-1 bg-[var(--n-line)]" aria-hidden="true" />
                      </div>
                      {cands.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5" data-testid="notes-tags-cascade-group">
                          {cands.map(renderTagChip)}
                        </div>
                      ) : (
                        <p className="px-1.5 py-1 text-[12px] text-[var(--n-muted-2)]" data-testid="notes-tags-cascade-empty">
                          {q ? 'No matching tags.' : 'No other tags appear on these notes.'}
                        </p>
                      )}
                    </>
                  );
                }
                // Default mode: all tags (own + shared-only), filtered by search.
                const own = ownTags.filter(matchQ);
                const shared = sharedOnlyTags.filter(matchQ);
                if (tags.length > 0 && own.length === 0 && shared.length === 0) {
                  return <p className="px-1.5 py-1 text-[12px] text-[var(--n-muted-2)]" data-testid="notes-tags-search-empty">No matching tags.</p>;
                }
                return (
                  <>
                    {own.length > 0 && (
                      <div className="flex flex-wrap gap-1.5" data-testid="notes-tags-own-group">
                        {own.map(renderTagChip)}
                      </div>
                    )}
                    {shared.length > 0 && (
                      <>
                        <div className={`${own.length > 0 ? 'mt-3' : ''} mb-1.5 flex items-center gap-2`} data-testid="notes-tags-shared-divider">
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">
                            <Users2 size={10} /> Shared
                          </span>
                          <span className="h-px flex-1 bg-[var(--n-line)]" aria-hidden="true" />
                        </div>
                        <div className="flex flex-wrap gap-1.5" data-testid="notes-tags-shared-group">
                          {shared.map(renderTagChip)}
                        </div>
                      </>
                    )}
                  </>
                );
              })()}
            </div>
            </>)}
          </div>
        </aside>
        </>
        )}

        {/* Middle — search + notes list */}
        {!focusMode && (
        <section className={`${selectedId ? 'hidden md:flex' : 'flex'} w-full md:w-[380px] shrink-0 flex-col border-r border-[var(--n-line)] bg-[var(--n-canvas)]`} data-testid="notes-list-column">
          <div className="border-b border-[var(--n-line)] p-3">
            <div className="mb-2 flex items-center justify-between" data-testid="notes-refresh-bar">
              <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium ${refreshPaused ? 'text-[var(--n-muted)]' : 'text-[var(--n-muted-2)]'}`} data-testid="notes-refresh-timer" title={refreshPaused ? 'Auto-refresh is paused — click play to resume' : 'Shared notes refresh automatically every 5 minutes'}>
                {refreshPaused ? (
                  <><Pause size={11} className="text-[var(--n-accent)]" /> Auto-refresh paused</>
                ) : (
                  <><RefreshCw size={11} className={refreshing ? 'animate-spin text-[var(--n-accent)]' : ''} />
                  {refreshing ? 'Refreshing…' : `Auto-refresh in ${Math.floor(secondsToRefresh / 60)}:${String(secondsToRefresh % 60).padStart(2, '0')}`}</>
                )}
              </span>
              <div className="flex items-center gap-0.5">
                <button onClick={toggleRefreshPause} title={refreshPaused ? 'Resume auto-refresh' : 'Pause auto-refresh'}
                  aria-label={refreshPaused ? 'Resume auto-refresh' : 'Pause auto-refresh'} aria-pressed={refreshPaused} data-testid="notes-refresh-pause-toggle"
                  className={`n-focus inline-flex h-7 w-7 items-center justify-center rounded-lg transition-colors ${refreshPaused ? 'text-[var(--n-accent)] bg-[var(--n-accent-bg)]' : 'text-[var(--n-muted-2)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-accent)]'}`}>
                  {refreshPaused ? <Play size={14} /> : <Pause size={14} />}
                </button>
                <button onClick={refreshAll} disabled={refreshing} title="Refresh now" aria-label="Refresh now" data-testid="notes-manual-refresh"
                  className="n-focus inline-flex h-7 w-7 items-center justify-center rounded-lg text-[var(--n-muted-2)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-accent)] disabled:opacity-50 transition-colors">
                  <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
                </button>
              </div>
            </div>
            <div className="relative" ref={searchRef}>
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--n-muted-2)]" />
              <input value={search}
                onChange={(e) => { setSearch(e.target.value); setSearchOpen(true); }}
                onFocus={() => setSearchOpen(true)}
                placeholder="Search notes, tags…" data-testid="notes-search-input"
                className="n-focus w-full rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] pl-9 pr-9 py-2.5 text-sm text-[var(--n-ink)] placeholder:text-[var(--n-muted-2)]" />
              {searchLoading && search.trim().length > 0 && (
                <Loader2 size={15} className="absolute right-9 top-1/2 -translate-y-1/2 animate-spin text-[var(--n-muted-2)]" data-testid="notes-search-loading" />
              )}
              {search.trim().length > 0 && (
                <button onClick={() => { setSearch(''); setSearchOpen(false); }} aria-label="Clear search"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--n-muted-2)] hover:text-[var(--n-ink)]"><X size={14} /></button>
              )}
              {searchOpen && search.trim().length > 0 && (() => {
                const q = search.trim().toLowerCase();
                // When tags are already selected, only suggest tags that co-occur
                // with the selection (so a chosen tag always returns notes).
                const tagBase = selectedTags.length
                  ? (coTagCounts ? Object.keys(coTagCounts).map((tg) => ({ tag: tg, count: coTagCounts[tg] })) : [])
                  : tags;
                const tagSug = tagBase.filter((t) => t.tag.toLowerCase().includes(q) && !selectedTags.includes(t.tag)).slice(0, 6);
                return (
                  <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[60vh] overflow-y-auto n-scroll rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1 shadow-[var(--n-shadow)]" data-testid="notes-search-suggestions">
                    {noteSug.length > 0 && (
                      <>
                        <div className="px-2.5 pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">Notes</div>
                        {noteSug.map((n) => (
                          <button key={n.id} onClick={() => { openNote(n.id); setSearchOpen(false); }} data-testid="notes-search-note-option"
                            className="flex w-full flex-col items-start gap-0.5 rounded-lg px-2.5 py-2 text-left hover:bg-[var(--n-canvas-2)]">
                            <div className="flex w-full items-center gap-2">
                              <FileText size={14} className="shrink-0 text-[var(--n-muted-2)]" />
                              <span className="min-w-0 flex-1 truncate text-sm font-medium text-[var(--n-ink)]"><Highlight text={n.title || 'Untitled'} term={search.trim()} /></span>
                              <span className="inline-flex shrink-0 items-center gap-1 text-[10px] text-[var(--n-muted-2)]"><Folder size={10} /> {n.folder_name || 'All Notes'}</span>
                            </div>
                            {n.snippet && (
                              <span className="line-clamp-1 pl-6 text-[12px] leading-relaxed text-[var(--n-muted)]" data-testid="notes-search-snippet"><Highlight text={n.snippet} term={search.trim()} /></span>
                            )}
                            {n.shared_by && (
                              <span className="inline-flex items-center gap-1 pl-6 text-[10px] text-[var(--n-accent)]"><Users2 size={10} /> {n.shared_by}</span>
                            )}
                          </button>
                        ))}
                      </>
                    )}
                    {noteSug.length > 0 && tagSug.length > 0 && (
                      <div className="my-1 h-px bg-[var(--n-line)]" data-testid="notes-search-divider" />
                    )}
                    {tagSug.length > 0 && (
                      <>
                        <div className="px-2.5 pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">Tags</div>
                        {tagSug.map((t) => (
                          <button key={t.tag} onClick={() => { setSelectedTags((prev) => (prev.includes(t.tag) ? prev : [...prev, t.tag])); setSearch(''); setSearchOpen(false); }} data-testid="notes-search-tag-option"
                            className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">
                            <Hash size={13} className="shrink-0 text-[var(--n-accent)]" />
                            <span className="flex-1 truncate">{t.tag}</span>
                            <span className="text-[10px] text-[var(--n-muted-2)]">{t.count}</span>
                          </button>
                        ))}
                      </>
                    )}
                    {searchLoading && noteSug.length === 0 && (
                      <div className="flex items-center justify-center gap-2 px-2.5 py-4 text-[12px] text-[var(--n-muted)]" data-testid="notes-search-loading-row"><Loader2 size={14} className="animate-spin" /> Searching…</div>
                    )}
                    {!searchLoading && noteSug.length === 0 && tagSug.length === 0 && (
                      <div className="px-2.5 py-3 text-center text-[12px] text-[var(--n-muted)]" data-testid="notes-search-nomatch">No matches for “{search.trim()}”.</div>
                    )}
                  </div>
                );
              })()}
            </div>
            {selectedTags.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="notes-active-tag-filters">
                {selectedTags.map((t, i) => (
                  <React.Fragment key={t}>
                    {i > 0 && <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--n-muted-2)]" data-testid="notes-tag-and">and</span>}
                    <span className="inline-flex items-center gap-1 rounded-full bg-[var(--n-accent-bg)] px-2 py-0.5 text-[11px] font-medium text-[var(--n-accent)]">
                      #{t}
                      <button onClick={() => toggleTag(t)} className="opacity-70 hover:opacity-100" aria-label={`Remove ${t}`}><X size={10} /></button>
                    </span>
                  </React.Fragment>
                ))}
                {selectedTags.length > 1 && (
                  <span className="text-[10px] text-[var(--n-muted-2)]">· notes must have all</span>
                )}
                <button type="button" onClick={togglePinCurrent} data-testid="notes-pin-combo"
                  title={isCurrentPinned ? 'Unpin this tag combination' : 'Pin this tag combination'}
                  className={`n-focus ml-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${isCurrentPinned ? 'border-[var(--n-accent)] bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'border-[var(--n-border)] text-[var(--n-muted)] hover:text-[var(--n-accent)]'}`}>
                  {isCurrentPinned ? <BookmarkCheck size={12} /> : <BookmarkPlus size={12} />}
                  {isCurrentPinned ? 'Pinned' : 'Pin'}
                </button>
              </div>
            )}
            {isSharedView && (
              <div className="mt-2 flex items-center gap-1.5 text-[11px] font-medium text-[var(--n-accent)]" data-testid="notes-shared-context">
                <Users2 size={12} /> Shared with you
              </div>
            )}
            {isPinnedView && (
              <div className="mt-2 flex items-center gap-1.5 text-[11px] font-medium text-[var(--n-accent)]" data-testid="notes-pinned-context">
                <Pin size={12} /> Pinned notes — from every folder
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto n-scroll pb-24 md:pb-0">
            {notesLoading ? (
              <div className="space-y-2 p-3" data-testid="notes-loading">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="rounded-xl border border-[var(--n-line)] p-3.5">
                    <div className="h-3.5 w-2/3 rounded bg-[var(--n-canvas-2)]" />
                    <div className="mt-2 h-2.5 w-full rounded bg-[var(--n-canvas-2)]" />
                    <div className="mt-1.5 h-2.5 w-4/5 rounded bg-[var(--n-canvas-2)]" />
                  </div>
                ))}
              </div>
            ) : notes.length === 0 ? (
              <div className="flex flex-col items-center justify-center px-6 py-16 text-center" data-testid="notes-empty-list">
                <BookOpen size={26} className="text-[var(--n-muted-2)]" />
                <p className="mt-3 text-sm font-medium text-[var(--n-ink-soft)]">
                  {isPinnedView ? 'No pinned notes yet' : isSharedView ? 'Nothing shared here yet' : (debounced || selectedTags.length ? 'No matching notes' : 'No notes yet')}
                </p>
                <p className="mt-1 text-[12px] text-[var(--n-muted)]">
                  {isPinnedView ? 'Pin a note to keep it at the top and find it here.' : isSharedView ? 'Notes others share with you will show up here.' : (debounced || selectedTags.length ? 'Try a different search or tag.' : 'Create your first note to get started.')}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-[var(--n-line)]" data-testid="notes-list">
                {displayNotes.map((n) => (
                  <NoteCard key={n.id} note={n} active={n.id === selectedId} onClick={() => openNote(n.id)} draggable={!isSharedView && !isPinnedView}
                    onTogglePin={togglePin} showFolder={isPinnedView}
                    onDragStart={(e) => { e.dataTransfer.setData('text/note-id', n.id); e.dataTransfer.effectAllowed = 'move'; }} />
                ))}
              </div>
            )}
          </div>
        </section>
        )}

        {/* Right — editor */}
        <main className={`${selectedId ? 'flex' : 'hidden'} md:flex min-w-0 flex-1 flex-col bg-[var(--n-surface)]`} data-testid="notes-editor-column">
          {noteLoading ? (
            <div className="flex flex-1 items-center justify-center text-[var(--n-muted)]"><Loader2 className="animate-spin" size={22} /></div>
          ) : selectedNote ? (
            <>
              <div className="flex items-center justify-between gap-2 border-b border-[var(--n-line)] px-3 py-2 md:px-5">
                <div className="flex min-w-0 items-center gap-2">
                  <button onClick={backToList} data-testid="notes-editor-back" aria-label="Back to notes list"
                    className="n-focus -ml-1 inline-flex md:hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">
                    <ChevronLeft size={20} />
                  </button>
                  {focusMode && (
                    <button onClick={toggleFocus} title="Exit focus mode" data-testid="notes-focus-exit"
                      className="n-focus hidden sm:inline-flex items-center gap-1.5 rounded-full border border-[var(--n-border)] px-2.5 py-1.5 text-[12px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">
                      <Minimize2 size={13} /> Exit focus
                    </button>
                  )}
                  {noteIsOwned ? (
                    <MoveFolderMenu folders={folders} sharedFolders={sharedFolders.filter((f) => f.permission === 'edit')} value={selectedNote.folder_id || ''} onChange={(fid) => saveNote({ folder_id: fid })} />
                  ) : (
                    <span className="inline-flex min-w-0 items-center gap-1.5 text-[12px] text-[var(--n-muted)]" data-testid="notes-editor-shared-label">
                      <Users2 size={13} className="shrink-0 text-[var(--n-accent)]" /> <span className="truncate">{selectedNote.shared_by ? `Shared by ${selectedNote.shared_by}` : 'Shared with you'}</span>
                    </span>
                  )}
                </div>
                <span className="shrink-0 text-[11px] text-[var(--n-muted-2)]">Edited {relTime(selectedNote.updated_at)}</span>
              </div>
              <div className="min-h-0 flex-1">
                <NoteEditor
                  key={`${selectedNote.id}:${remoteReloadKey}`}
                  note={selectedNote}
                  folders={folders}
                  allTags={allTags}
                  onSave={saveNote}
                  onDelete={askDeleteNote}
                  onToggleFavorite={toggleFavorite}
                  onAttachmentsChanged={handleAttachmentsChanged}
                  onOpenShare={openShare}
                  onRestored={handleVersionRestored}
                  presence={presence[selectedNote.id] || []}
                  currentUserId={principal?.id}
                />
              </div>
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center px-6 text-center" data-testid="notes-editor-empty">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--n-accent-bg)]">
                <Feather size={24} className="text-[var(--n-accent)]" />
              </div>
              <h2 className="mt-4 font-display text-2xl text-[var(--n-ink)]">A calm place to think</h2>
              <p className="mt-1.5 max-w-xs text-sm text-[var(--n-muted)]">Select a note from the list, or start a new one.</p>
              <div className="mt-5 flex items-center gap-2">
                {focusMode && (
                  <button onClick={toggleFocus} data-testid="notes-editor-empty-exit-focus"
                    className="n-focus inline-flex items-center gap-2 rounded-xl border border-[var(--n-border)] px-4 py-2.5 text-sm font-semibold text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)] transition-colors">
                    <Minimize2 size={16} /> Exit focus
                  </button>
                )}
                <button onClick={() => setTemplatePicker(true)} data-testid="notes-editor-empty-new-button"
                  className="n-focus inline-flex items-center gap-2 rounded-xl bg-[var(--n-ink)] px-4 py-2.5 text-sm font-semibold text-[var(--n-canvas)] hover:opacity-90 transition-opacity">
                  <Plus size={16} /> New note
                </button>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* ---- Dialogs ---- */}
      <FolderDialog open={folderDialog.open} initial={folderDialog.initial}
        onClose={() => setFolderDialog({ open: false, initial: null, parentId: null })} onSubmit={submitFolder} />
      <TemplatePickerDialog open={templatePicker} onClose={() => setTemplatePicker(false)} onPick={createNote} />
      <TemplateManagerDialog open={templateManager} onClose={() => setTemplateManager(false)}
        currentNote={selectedNote} onChanged={() => {}} />
      <AdminUsersDialog open={adminUsers} onClose={() => setAdminUsers(false)} />
      <ShareDialog open={shareDialog.open} resource={shareDialog.resource}
        onClose={() => setShareDialog({ open: false, resource: null })} notify={notify} />
      <ConfirmDialog open={!!confirm.open} title={confirm.title} message={confirm.message}
        confirmLabel={confirm.confirmLabel} danger={confirm.danger}
        onConfirm={confirm.onConfirm} onClose={() => setConfirm({ open: false })} />

      {/* Command Palette (Cmd/Ctrl+K or /) — notes + folders + tags + pinned */}
      <CommandPalette
        open={cmdkOpen}
        onClose={() => setCmdkOpen(false)}
        folders={flattenFolderTree(folders)}
        sharedFolders={sharedFolders}
        tags={tags}
        searchFn={async (term) => {
          const { data } = await notesApi.get(`/notes/notes/search?q=${encodeURIComponent(term)}&limit=8`);
          return data || [];
        }}
        onOpenNote={(id) => { openNote(id); }}
        onSelectFolder={(id) => { setView(id); }}
        onSelectSharedFolder={(id) => { setSharedInbox(0); setView(`shared:${id}`); }}
        onSelectTag={(t) => { setSelectedTags((prev) => (prev.includes(t) ? prev : [...prev, t])); if (view !== 'all') setView('all'); }}
        onGoView={(v) => { setView(v); }}
      />

      {/* ---- Due reminder alerts (prominent, stacked above the toast) ---- */}
      {dueQueue.length > 0 && (
        <div className="fixed bottom-5 right-5 z-[320] flex w-[320px] max-w-[calc(100vw-2.5rem)] flex-col gap-2" data-testid="notes-reminder-alert-stack">
          {dueQueue.slice(0, 4).map((n) => (
            <div key={remKey(n)} className="rounded-2xl border-2 border-[var(--n-accent)] bg-[var(--n-surface)] p-4 shadow-[var(--n-shadow)]" data-testid="notes-reminder-alert" role="alert">
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--n-accent-bg)] text-[var(--n-accent)]">
                  <BellRing size={18} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-accent)]">Reminder due</div>
                  <div className="mt-0.5 truncate text-sm font-semibold text-[var(--n-ink)]" title={n.title || 'Untitled'}>{n.title || 'Untitled'}</div>
                  {n.owned === false && n.shared_by && (
                    <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-[var(--n-accent)]" data-testid="notes-reminder-alert-shared-by">
                      <Users2 size={11} /> Shared by {n.shared_by}
                    </div>
                  )}
                  {n.reminder_at && <div className="text-[11px] text-[var(--n-muted)]">{formatWhen(n.reminder_at)}{n.reminder_repeat && n.reminder_repeat !== 'none' ? ` · repeats ${n.reminder_repeat}` : ''}</div>}
                  <div className="mt-2.5 flex items-center gap-2">
                    <button onClick={() => openReminder(n)} data-testid="notes-reminder-alert-open"
                      className="n-focus inline-flex items-center gap-1.5 rounded-lg bg-[var(--n-accent)] px-3 py-1.5 text-[12px] font-semibold text-white hover:opacity-90">
                      Open note
                    </button>
                    <button onClick={() => acknowledgeReminder(n)} data-testid="notes-reminder-alert-dismiss"
                      className="n-focus rounded-lg px-3 py-1.5 text-[12px] font-medium text-[var(--n-muted)] hover:bg-[var(--n-canvas-2)]">Dismiss</button>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="notes-reminder-snooze">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]">Snooze</span>
                    <button onClick={() => snoozeReminder(n, 10)} data-testid="notes-reminder-snooze-10m"
                      className="n-focus rounded-md border border-[var(--n-border)] px-2 py-0.5 text-[11px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">10 min</button>
                    <button onClick={() => snoozeReminder(n, 60)} data-testid="notes-reminder-snooze-1h"
                      className="n-focus rounded-md border border-[var(--n-border)] px-2 py-0.5 text-[11px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">1 hour</button>
                    <button onClick={() => snoozeUntilTomorrow(n)} data-testid="notes-reminder-snooze-tomorrow"
                      className="n-focus rounded-md border border-[var(--n-border)] px-2 py-0.5 text-[11px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">Tomorrow 10am</button>
                    <button
                      onClick={() => { const k = remKey(n); setCustomSnoozeKey((cur) => (cur === k ? null : k)); setCustomSnoozeVal(toLocalInputValue(Date.now() + 3600000)); }}
                      data-testid="notes-reminder-snooze-custom-toggle"
                      className={`n-focus inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-medium transition-colors
                        ${customSnoozeKey === remKey(n) ? 'border-[var(--n-accent)] bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'border-[var(--n-border)] text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
                      <CalendarClock size={12} /> Custom
                    </button>
                  </div>
                  {customSnoozeKey === remKey(n) && (
                    <div className="mt-2 flex items-center gap-1.5" data-testid="notes-reminder-snooze-custom">
                      <input type="datetime-local" value={customSnoozeVal} min={toLocalInputValue(Date.now())}
                        onChange={(e) => setCustomSnoozeVal(e.target.value)} data-testid="notes-reminder-snooze-custom-input"
                        className="n-focus min-w-0 flex-1 rounded-md border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-1 text-[12px] text-[var(--n-ink)]" />
                      <button onClick={() => snoozeCustom(n, customSnoozeVal)} data-testid="notes-reminder-snooze-custom-set"
                        className="n-focus rounded-md bg-[var(--n-accent)] px-2.5 py-1 text-[12px] font-semibold text-white hover:opacity-90">Set</button>
                    </div>
                  )}
                </div>
                <button onClick={() => acknowledgeReminder(n)} aria-label="Close" className="text-[var(--n-muted-2)] hover:text-[var(--n-ink)]"><X size={15} /></button>
              </div>
            </div>
          ))}
          {dueQueue.length > 4 && (
            <div className="rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-3 py-2 text-center text-[11px] text-[var(--n-muted)]" data-testid="notes-reminder-alert-more">
              +{dueQueue.length - 4} more due
            </div>
          )}
        </div>
      )}

      {/* ---- Local toast ---- */}
      {toast && (
        toast.action ? (
          <button onClick={() => { toast.action(); setToast(null); }} data-testid="notes-toast"
            className="fixed bottom-5 right-5 z-[300] flex items-center gap-2 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-4 py-3 text-left text-sm text-[var(--n-ink)] shadow-[var(--n-shadow)] hover:bg-[var(--n-canvas-2)]">
            <Clock size={14} className="text-[var(--n-accent)]" /> {toast.message}
          </button>
        ) : (
          <div className="fixed bottom-5 right-5 z-[300] rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-4 py-3 text-sm text-[var(--n-ink)] shadow-[var(--n-shadow)]" data-testid="notes-toast">
            {toast.message}
          </div>
        )
      )}

      {/* ---- Web Push opt-in prompt (one-time, respects browser permission) ---- */}
      <PushPrompt onToast={notify} />
      <PlannerReminderAlerts />
      <PlannerNavShortcuts />
      {chatEnabled && <ChatDock open={chatOpen} onClose={() => setChatOpen(false)} onUnread={setChatUnread} onReserveChange={setChatReserve} />}

      {/* Mobile bottom navigation — shown on the list view (thumb-reachable) */}
      {!selectedId && (
        <nav data-testid="notes-mobile-bottombar"
          className="fixed inset-x-0 bottom-0 z-[140] flex items-stretch border-t border-[var(--n-line)] bg-[var(--n-surface)]/95 backdrop-blur-md md:hidden"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          <button type="button" data-testid="notes-bottombar-new" onClick={() => setTemplatePicker(true)}
            className="n-focus flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[var(--n-accent)]">
            <span className="grid h-8 w-8 place-items-center rounded-full bg-[var(--n-accent)] text-white"><Plus size={18} /></span>
            <span className="text-[10.5px] font-semibold">New</span>
          </button>
          <button type="button" data-testid="notes-bottombar-search"
            onClick={() => { setMobileRailOpen(false); requestAnimationFrame(() => { const el = searchRef.current ? searchRef.current.querySelector('input') : null; if (el) { el.scrollIntoView({ block: 'center' }); el.focus(); setSearchOpen(true); } }); }}
            className="n-focus flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[var(--n-muted)] hover:text-[var(--n-ink)]">
            <Search size={19} />
            <span className="text-[10.5px] font-medium">Search</span>
          </button>
          <button type="button" data-testid="notes-bottombar-tasks" onClick={() => navigate('/notes/tasks')}
            className="n-focus flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[var(--n-muted)] hover:text-[var(--n-ink)]">
            <ListChecks size={19} />
            <span className="text-[10.5px] font-medium">Tasks</span>
          </button>
          {chatEnabled && (
            <button type="button" data-testid="notes-bottombar-chat" onClick={() => setChatOpen(true)}
              className="relative n-focus flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[var(--n-muted)] hover:text-[var(--n-ink)]">
              <MessageSquare size={19} />
              {chatUnread > 0 && <span className="absolute right-1/2 top-1 grid h-4 min-w-4 translate-x-3 place-items-center rounded-full px-1 text-[9px] font-bold text-white" style={{ background: 'var(--n-danger)' }}>{chatUnread > 99 ? '99+' : chatUnread}</span>}
              <span className="text-[10.5px] font-medium">Chat</span>
            </button>
          )}
        </nav>
      )}
    </div>
  );
}
